/* assets/wl__core.js
   Weight Loss Program Hub — Core JS
   - Modal controller (open/close)
   - BMI calculator + weekly log (localStorage for now; upgrade path to metafields later)
   - BMR + Calorie Plan (Mifflin + activity multiplier, safety minimums)
   - Macro Targets (percent + grams), fed by BMR "Use" buttons
   - Add-ons quick-shop modal (product data, variants, add to cart)
   - Peptide selector quiz (goal-based matching against the Add-ons catalog)
*/

// -----------------------------
// Peptide quiz goal categories + editorial tagging
// (price/availability/title come live from Shopify via #wl-addons-catalog;
// this is just "which goals this product addresses" — update when new
// products are added to the anti-aging collection.)
// -----------------------------
const WL_QUIZ_GOALS = [
  { key: "immune", label: "Stay Healthy, Boost Immune System" },
  { key: "fat_loss", label: "Fat Loss / Metabolic" },
  { key: "lean_mass", label: "Muscle Gain / Lean Mass" },
  { key: "recovery", label: "Recovery / Healing" },
  // Plain-English label (Derek 2026-09-03): pain/arthritis seekers must
  // recognize this goal — routes to BPC-157 / Wolverine / GLOW.
  { key: "anti_inflammatory", label: "Pain, Inflammation & Arthritis" },
  { key: "anti_aging", label: "Anti-aging / Longevity" },
  { key: "energy", label: "Energy / Mitochondrial" },
  { key: "cognition", label: "Neurocognition / Mood" },
  { key: "skin_hair", label: "Skin / Hair" },
  { key: "hair_loss", label: "Hair Loss" },
  { key: "libido", label: "Libido / Hormonal" },
];

// dosing values below are generic, community-cited starting points (same
// style/source class as public peptide reference tools) — NOT Reflections'
// clinical protocol. Review and correct every field with your clinical team
// before relying on this in front of patients.
const PEPTIDE_TAGS = {
// ---- PREMIXED / DESIGNER LINE (2026-08-28, dosing per
// PeptideProtocol/premixed-line/DOSING-LOCKS.md; tiers tier-everyday /
// tier-premium / tier-cutting-edge are the catalog axis) ----
  "titan-blend": {
    categories: ["lean_mass", "recovery", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "15–25 units (300–500 mcg tesamorelin side)", frequency: "5 nights a week (weekends off)", administration: "Subcutaneous injection — pre-mixed, nothing to reconstitute", bestTime: "Before bed, fasted (aligns with the overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site reactions, flushing, water retention; can raise blood sugar. Start at a HALF dose for the first 3 nights." },
  },
  "serenity-blend": {
    categories: ["cognition", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "15–25 units (150–250 mcg DSIP/Selank side)", frequency: "Nightly, as needed", administration: "Subcutaneous injection — pre-mixed", bestTime: "30–60 minutes before bed", comingOff: "No taper needed", sideEffects: "Drowsiness (by design), vivid dreams, injection-site irritation. GH-axis components — start at a half dose the first 3 nights." },
  },
  "synapse-blend": {
    categories: ["cognition", "energy"], injectable: true, caution: [],
    dosing: { dose: "15–25 units (150–250 mcg Semax/Selank + 300–500 mcg MOTS-c)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or early afternoon — can be stimulating", comingOff: "No taper needed", sideEffects: "Injection-site irritation; can feel stimulating, so avoid evenings. Half dose the first 3 days." },
  },
  "focus-blend": {
    categories: ["cognition"], injectable: true, caution: [],
    dosing: { dose: "20–30 units (200–300 mcg each of Semax and Selank)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or early afternoon", comingOff: "No taper needed", sideEffects: "Generally well tolerated; can be mildly stimulating" },
  },
  "elevate-blend": {
    categories: ["recovery", "anti_inflammatory", "cognition"], injectable: true, caution: [],
    dosing: { dose: "12–20 units (240–400 mcg BPC-157/KPV side — the gut pair sets the dose)", frequency: "Daily", administration: "Subcutaneous injection — pre-mixed", bestTime: "Any time; consistency matters more than timing", comingOff: "Typically run in 4–8 week cycles", sideEffects: "Generally well tolerated; injection-site irritation" },
  },
  "eternal-blend": {
    categories: ["skin_hair", "anti_aging", "recovery"], injectable: true, caution: [],
    dosing: { dose: "12–20 units (1.2–2 mg GHK-Cu side)", frequency: "Daily", administration: "Subcutaneous injection — pre-mixed", bestTime: "Any time, consistent daily slot", comingOff: "No taper needed", sideEffects: "Injection-site irritation; copper peptides can sting on the way in" },
  },
  "euphoria-blend": {
    categories: ["energy", "libido", "skin_hair"], injectable: true, caution: [],
    dosing: { dose: "12–20 units (240–400 mcg MOTS-c side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or before exercise", comingOff: "No taper needed", sideEffects: "Injection-site irritation, mild flushing" },
  },
  "fortify-blend": {
    categories: ["immune", "recovery"], injectable: true, caution: [],
    dosing: { dose: "20–30 units (200–300 mcg Thymosin Alpha-1 side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Any time of day", comingOff: "Often run seasonally — reassess with your provider", sideEffects: "Generally well tolerated. NOT for autoimmune conditions, immunosuppressant therapy, or transplant recipients — talk to your provider first." },
  },
  "fusion-blend": {
    categories: ["fat_loss", "energy", "recovery"], injectable: true, caution: [],
    dosing: { dose: "17–33 units (0.5–1 mg tesamorelin side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning, fasted", comingOff: "Can stop without a taper", sideEffects: "Injection-site reactions, water retention; can raise blood sugar. Half dose the first 3 days." },
  },
  "ascend-blend": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "15–25 units (300–500 mcg tesamorelin + 30–50 mcg IGF-1 LR3)", frequency: "5 nights a week (weekends off)", administration: "Subcutaneous injection — pre-mixed", bestTime: "Before bed, fasted", comingOff: "Can stop without a taper", sideEffects: "Injection-site reactions, water retention, possible blood-sugar changes. Start at a HALF dose for the first 3 nights." },
  },
  "glutathione-injectable": {
    categories: ["anti_aging", "immune", "energy"], injectable: true, caution: [],
    dosing: { dose: "200–600 mg per push (100–300 units on the insulin syringe — this one is a larger draw)", frequency: "2x per week", administration: "Subcutaneous or slow IM push — pre-mixed", bestTime: "Any time of day", comingOff: "No taper needed", sideEffects: "Generally well tolerated; occasional GI upset or injection-site soreness" },
  },
  "slu-pp-332": {
    categories: ["energy", "fat_loss", "lean_mass"], injectable: true, caution: [],
    dosing: { dose: "300–500 mcg (15–25 units on the 10 mg vial)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or before exercise", comingOff: "Cycle-based — reassess monthly with your provider", sideEffects: "An exercise-mimetic research compound with limited human data — report anything unusual to your provider" },
  },
  "ss-31-elamipretide": {
    categories: ["energy", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "5–15 mg (10–30 units on the 300 mg vial)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning", comingOff: "No taper needed", sideEffects: "Injection-site reactions; generally well tolerated in trials" },
  },
  "melanotan-ii": {
    categories: ["skin_hair", "libido"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "250 mcg to start (12.5 units); 500 mcg–1 mg once established", frequency: "3x per week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Evening — the early-dose nausea passes overnight", comingOff: "The tan fades over weeks after stopping", sideEffects: "Nausea and flushing on the first injections, spontaneous erections. DARKENS EXISTING MOLES — monitor your skin and show us any change; melanocortins have case reports of atypical moles." },
  },

  // ---- 2026-08-30 WAVE (16 SKUs from the Peptide Catalog session; the old
  // sermorelin-1 + sermorelin keys were RETIRED from browsing and replaced
  // by sermorelin-injectable + sermorelin-odt. Dosing: DOSING-LOCKS.md for
  // the injectables, per-capsule/spray strengths from the variant data.
  // The needle-free entries are THE POINT — Derek: "make sure the nasal
  // sprays and the capsules are highlighted and put in the results for
  // people that don't want to give themselves injections." ----
  "sermorelin-injectable": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "200–300 mcg per injection (10 mg vial to mix, or 15 mg pre-mixed)", frequency: "5 nights a week (weekends off)", administration: "Subcutaneous injection", bestTime: "Before bed, fasted (aligns with natural overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site redness, flushing, mild headache" },
  },
  "sermorelin-odt": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 tablet (1 mg) dissolved under the tongue — don't swallow it; it absorbs through the tissue there", frequency: "5 nights a week (weekends off)", administration: "Orally disintegrating tablet — needle-free", bestTime: "Before bed, fasted (aligns with the overnight GH pulse)", comingOff: "Can stop without a taper", sideEffects: "Mild flushing or headache; generally well tolerated" },
  },
  "shred-blend": {
    categories: ["fat_loss", "lean_mass", "energy"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "15–25 units (AOD-9604 300–500 mcg / MOTS-c 300–500 mcg side)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed, nothing to reconstitute", bestTime: "Morning, fasted — before exercise if you train", comingOff: "No taper needed", sideEffects: "Injection-site irritation, flushing; GH-axis components can raise blood sugar. Start at a HALF dose for the first 3 days." },
  },
  "ignite-blend": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [],
    dosing: { dose: "6–10 units (MOTS-c 300–500 mcg side — NAD+ 3–5 mg and 5-Amino-1MQ 0.3–0.5 mg ride along)", frequency: "5 days a week", administration: "Subcutaneous injection — mix the vial with 2 mL bacteriostatic water", bestTime: "Morning, fasted — before exercise if you train", comingOff: "No taper needed", sideEffects: "Injection-site irritation; NAD+ can cause brief flushing — a slower push helps. No growth-hormone components, so none of SHRED's blood-sugar or cancer-history cautions. Start at a HALF dose for the first 3 days." },
  },
  "semax": {
    categories: ["cognition", "energy"], injectable: true, caution: [],
    dosing: { dose: "300–500 mcg per injection (15–25 units)", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning or early afternoon — can be stimulating", comingOff: "No taper needed", sideEffects: "Injection-site irritation; avoid evenings if it keeps you up" },
  },
  "5-amino-1mq": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [],
    dosing: { dose: "500 mcg–1 mg to start (10–20 units on the 25 mg vial); up to 1.5 mg once established", frequency: "5 days a week", administration: "Subcutaneous injection — pre-mixed; two vial sizes (5 mg trial / 25 mg standard)", bestTime: "Morning", comingOff: "No taper needed", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  "thymalin": {
    categories: ["immune", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "~10 mg daily (100 units) — run as a COURSE, classically about 10 days", frequency: "Daily for the length of the course, then stop", administration: "Subcutaneous injection — pre-mixed", bestTime: "Morning", comingOff: "Ending the course IS the protocol — repeat once or twice a year", sideEffects: "Injection-site irritation; on immunosuppressants or with an autoimmune condition, talk to us first" },
  },
  "5-amino-1mq-capsules": {
    categories: ["fat_loss", "energy"], injectable: false, caution: [],
    dosing: { dose: "1 capsule (50 mg)", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning, with or without food", comingOff: "No taper needed", sideEffects: "Mild GI upset possible; generally well tolerated" },
  },
  "bpc-157-tb-500-capsules": {
    categories: ["recovery", "anti_inflammatory"], injectable: false, caution: [],
    dosing: { dose: "1 capsule, 1–2x daily", frequency: "Daily", administration: "Oral capsule", bestTime: "On an empty stomach — the oral route is strongest for gut-focused use", comingOff: "Typically run 4–8 week cycles", sideEffects: "Generally well tolerated" },
  },
  "dihexa-capsules": {
    categories: ["cognition"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 capsule (5 mg)", frequency: "Daily to every other day", administration: "Oral capsule", bestTime: "Morning, with a meal containing fat", comingOff: "Cycle-based — take breaks between courses", sideEffects: "Headache or vivid dreams reported; human data is thin — this is a frontier compound" },
  },
  "dihexa-tesofensine-capsules": {
    categories: ["cognition", "fat_loss", "energy"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 capsule (5 mg dihexa / 500 mcg tesofensine)", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning — tesofensine is stimulating, avoid evenings", comingOff: "Appetite effects fade over days; no formal taper", sideEffects: "Raised heart rate and blood pressure, dry mouth, insomnia — use caution with hypertension" },
  },
  "tesofensine-capsules": {
    categories: ["fat_loss", "energy"], injectable: false, caution: [],
    dosing: { dose: "1 capsule (500 mcg)", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning — stimulating, avoid evenings", comingOff: "Appetite effects fade over days; no formal taper", sideEffects: "Raised heart rate and blood pressure, dry mouth, insomnia — use caution with hypertension" },
  },
  "slu-pp-332-bam15-capsules": {
    categories: ["fat_loss", "energy"], injectable: false, caution: [],
    dosing: { dose: "1 capsule (100 mcg SLU-PP-332 / 15 mg BAM15)", frequency: "Daily, 5 days a week", administration: "Oral capsule", bestTime: "Morning — pairs naturally with training days", comingOff: "No taper needed", sideEffects: "Warmth or flushing possible (BAM15 is a mitochondrial uncoupler); human data is thin — frontier territory" },
  },
  "selank-nasal-spray": {
    categories: ["cognition"], injectable: false, caution: [],
    dosing: { dose: "1–2 sprays per dose (2.5 mg/mL)", frequency: "1–2x daily, as needed", administration: "Intranasal spray — the route the original research used", bestTime: "Morning and/or early afternoon", comingOff: "No taper needed", sideEffects: "Brief nasal irritation; generally well tolerated" },
  },
  "semax-nasal-spray": {
    categories: ["cognition", "energy"], injectable: false, caution: [],
    dosing: { dose: "1–2 sprays per dose (2.5 mg/mL)", frequency: "1–2x daily", administration: "Intranasal spray — needle-free, ready to use", bestTime: "Morning and/or early afternoon — can be stimulating", comingOff: "No taper needed", sideEffects: "Brief nasal irritation; avoid evenings if it keeps you up" },
  },
  "methylene-blue": {
    categories: ["cognition", "energy", "anti_aging"], injectable: false, caution: ["pregnancy"],
    dosing: { dose: "1 capsule (10 mg) — low-dose is the point; at high doses the redox behaviour reverses", frequency: "Daily", administration: "Oral capsule", bestTime: "Morning", comingOff: "No taper needed", sideEffects: "Blue-green urine is normal. Do NOT combine with SSRIs/SNRIs or MAOIs (serotonin-syndrome risk), and skip it with G6PD deficiency" },
  },
  "mk-677-ibutamoren": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "1 capsule (12.5 or 25 mg — start at 12.5)", frequency: "Once nightly", administration: "Oral capsule", bestTime: "Before bed — the GH pulse is overnight, and the hunger side rides through sleep", comingOff: "Can stop without a taper", sideEffects: "Water retention, increased appetite, vivid dreams; can raise blood sugar — worth watching if metabolic health is a concern" },
  },
  "tadalafil": {
    categories: ["libido"], injectable: false, caution: [],
    dosing: { dose: "1 tablet daily (5 or 10 mg — most start at 5)", frequency: "Once daily — the long half-life is the point: nothing to time around occasions", administration: "Oral tablet", bestTime: "Same time each day, with or without food", comingOff: "No taper — effects fade within a few days of stopping", sideEffects: "Headache, flushing, stuffy nose, muscle aches. NEVER combine with nitrates (dangerous blood-pressure drop) — and flag any heart condition or alpha-blocker to your provider" },
  },
  "tesamorelin": {
    categories: ["fat_loss", "anti_aging", "lean_mass"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "0.5 mg per injection to start (0.5–1 mg typical)", frequency: "Daily", administration: "Subcutaneous injection", bestTime: "Before bed, fasted (aligns with natural overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site reactions, joint aches, fluid retention; can raise blood sugar — glucose is worth monitoring" },
  },
  "tesamorelin-ipamorelin": {
    categories: ["fat_loss", "lean_mass", "anti_aging", "recovery"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "0.5 mg tesamorelin per injection to start (0.5–1 mg typical)", frequency: "Daily", administration: "Subcutaneous injection — comes pre-mixed, nothing to reconstitute", bestTime: "Before bed, fasted (aligns with natural overnight GH pulse)", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Injection-site reactions, joint aches, fluid retention; can raise blood sugar — glucose is worth monitoring" },
  },
  "thymosin-alpha-1": {
    categories: ["immune", "recovery", "anti_inflammatory", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "1.6 mg per injection", frequency: "Twice weekly; some use it daily for 5–7 days during an active illness, then stop", administration: "Subcutaneous injection", bestTime: "Any time — consistency matters more than timing", comingOff: "No taper needed; often run seasonally rather than continuously", sideEffects: "Injection-site reactions; generally well tolerated. ⚠ Autoimmune conditions, immunosuppressant medication or a transplant = provider conversation first" },
  },
  "enroll-in-trim-weight-loss-program-copy": {
    // requiresVisit (Derek 2026-08-30) — NOTE: on THIS portal surface the flag
    // is data-only by design: the visit banner was deliberately removed from
    // portal modals 2026-08-28 (Derek: "we've already seen them"), and the
    // public quiz section is the surface that renders it.
    categories: ["fat_loss"], injectable: true, caution: ["pregnancy"], requiresVisit: true,
    dosing: { dose: "~1 mg to start, titrated up per your provider's plan", frequency: "Once weekly", administration: "Subcutaneous injection", bestTime: "Any time of day, consistent weekly day", comingOff: "Taper down gradually to avoid rebound weight regain", sideEffects: "GI upset (nausea, constipation), increased heart rate" },
  },
  "wolverine-blend-10mg-bpc-157-10mg-tb-500-injectable-no-rx-required": {
    categories: ["recovery", "anti_inflammatory"], injectable: true, caution: [],
    dosing: { dose: "250–500 mcg BPC-157 + up to 2.5 mg TB-500 per injection", frequency: "Daily (BPC-157) / 2x weekly (TB-500)", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters more than timing", comingOff: "Typically run in 4–6 week cycles, reassess with your provider", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  "nad-patch": {
    categories: ["energy", "anti_aging"], injectable: false, caution: [],
    dosing: { dose: "1,200 mg NAD+ / 200 mg glutathione per patch", frequency: "Up to 1x weekly", administration: "Transdermal iontophoresis patch — worn overnight, needle-free", bestTime: "Apply before bed, remove in the morning", comingOff: "No taper needed", sideEffects: "Mild irritation at the application site" },
  },
  "nad-sublingual-tablets": {
    categories: ["energy", "anti_aging"], injectable: false, caution: [],
    dosing: { dose: "100 mg or 200 mg NAD+ with B12 200 mcg per tablet", frequency: "Daily", administration: "Sublingual — dissolves under the tongue, no water needed", bestTime: "Morning (may be energizing)", comingOff: "No taper needed", sideEffects: "Generally well tolerated; mild nausea possible" },
  },
  "nad-nasal-spray": {
    categories: ["energy", "anti_aging"], injectable: false, caution: [],
    dosing: { dose: "300 mg/mL — 7,500 mg per 25 mL bottle; your provider sets the number of sprays", frequency: "2–3x weekly", administration: "Intranasal spray — needle-free, the fastest of the three formats", bestTime: "Morning (may be energizing)", comingOff: "No taper needed", sideEffects: "Brief nasal irritation; mild flushing" },
  },
  "nad-injectable": {
    categories: ["energy", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "20 units (20 mg) per injection — 100 mg per week (comes pre-mixed at 100 mg/mL)", frequency: "5 days a week", administration: "Subcutaneous injection", bestTime: "Morning (may be energizing)", comingOff: "No taper needed", sideEffects: "Flushing, mild nausea — slower titration helps" },
  },
  "cjc1295-impramorelin-300-300-mcg-sublingual-2-month-supply": {
    categories: ["lean_mass", "anti_aging", "recovery"], injectable: false, caution: ["cancer_history"],
    dosing: { dose: "One sublingual capsule per dose", frequency: "Nightly", administration: "Sublingual — dissolves under the tongue (no injection)", bestTime: "Before bed, fasted", comingOff: "Can stop without a taper; effects fade gradually", sideEffects: "Generally well tolerated; mild water retention or flushing possible" },
  },
  "glow-stack-bpc-157-tb-500-ghk-cu-the-beauty-peptide": {
    categories: ["skin_hair", "recovery", "anti_inflammatory", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "Per vial ratio on the label", frequency: "Daily to a few times weekly, per protocol", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters", comingOff: "Typically run in cycles, reassess with your provider", sideEffects: "Injection-site irritation" },
  },
  "bpc-157-peptide-capsules-pharmaceutical-grade-recovery-without-prescription-copy": {
    categories: ["recovery", "anti_inflammatory"], injectable: true, caution: [],
    dosing: { dose: "250–500 mcg per injection", frequency: "1–2x daily", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters", comingOff: "Typically run 4–8 week cycles", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  "cjc-1295-no-dac-ipamorelin-5mg-5mg": {
    categories: ["lean_mass", "recovery", "anti_aging"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "100 mcg each (CJC-1295 + Ipamorelin) per injection", frequency: "Nightly", administration: "Subcutaneous injection", bestTime: "Before bed, fasted", comingOff: "No taper required", sideEffects: "Water retention, flushing, injection-site reactions" },
  },
  // PT-141 — product handle must be exactly "pt-141" in Shopify for this
  // entry (and the quiz) to pick it up. Dosing from the Peptide Playbook
  // snapshot (same source as the mixing calculator).
  "pt-141": {
    categories: ["libido"], injectable: true, caution: ["pregnancy"],
    dosing: { dose: "1–2 mg as needed (approved 1.75 mg)", frequency: "1–2x per week MAX — PT-141 is a melanocortin like Melanotan: frequent use tans skin and darkens moles (unless that effect is wanted)", administration: "Subcutaneous injection", bestTime: "~45 minutes before activity", comingOff: "As-needed use — no cycle or taper", sideEffects: "Nausea, flushing, headache; can raise blood pressure — use caution with hypertension" },
  },
  "mots-c": {
    categories: ["fat_loss", "energy", "libido"], injectable: true, caution: [],
    dosing: { dose: "0.5–1 mg per injection to start; 3–4 mg only after a month on it and with good cardiovascular health", frequency: "2–3x weekly", administration: "Subcutaneous injection", bestTime: "Before exercise", comingOff: "No taper required", sideEffects: "Injection-site irritation, mild fatigue" },
  },
  "mega-burn-lipotropic-injection": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [], requiresVisit: true,
    dosing: { dose: "1 cc (1 mL) per injection", frequency: "Weekly", administration: "Intramuscular injection", bestTime: "Morning", comingOff: "No taper required", sideEffects: "Injection-site soreness, mild GI upset" },
  },
  "b12-vial": {
    categories: ["fat_loss", "energy"], injectable: true, caution: [], requiresVisit: true,
    dosing: { dose: "0.25 cc (25 units on a U-100 insulin syringe)", frequency: "Weekly", administration: "Intramuscular injection", bestTime: "Morning", comingOff: "No taper required", sideEffects: "Injection-site soreness; rarely, mild flushing" },
  },
  "3-in-1-hair-restoration-formula": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "As directed on product label", frequency: "Daily", administration: "Topical", bestTime: "Per label instructions", comingOff: "No taper required", sideEffects: "Scalp irritation possible" },
  },
  "nutrafol-mens-hair-growth-supplements-clinically-tested-for-visibly-thicker-hair-and-scalp-coverage-dermatologist-recommended-1-month-supply-copy": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "4 capsules per day (per label)", frequency: "Daily", administration: "Oral", bestTime: "With a meal containing fat, for absorption", comingOff: "No taper required", sideEffects: "Generally well tolerated; mild GI upset possible" },
  },
  "glow-stack-ghk-cu-30mg-bpc-157-10mg-tb500-10mg-the-beauty-peptide-copy": {
    categories: ["skin_hair", "recovery", "anti_inflammatory", "anti_aging"], injectable: true, caution: [],
    dosing: { dose: "Per vial ratio on the label", frequency: "Daily to a few times weekly, per protocol", administration: "Subcutaneous injection", bestTime: "Any time, consistency matters", comingOff: "Typically run in cycles, reassess with your provider", sideEffects: "Injection-site irritation" },
  },
  "nutrafol-womens-vegan-hair-growth-supplements-plant-based-for-women-18-44-years-old-clinically-tested-for-visibly-thicker-stronger-hair-dermatologist-recommended-1-month-supply-1": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "4 capsules per day (per label)", frequency: "Daily", administration: "Oral", bestTime: "With a meal containing fat, for absorption", comingOff: "No taper required", sideEffects: "Generally well tolerated; mild GI upset possible" },
  },
  "selank-10-mg-the-brain-peptide": {
    categories: ["cognition"], injectable: false, caution: [],
    dosing: { dose: "100–200 mcg per spray, 2–3 sprays per dose", frequency: "1–2x daily", administration: "Intranasal spray", bestTime: "Morning and/or early afternoon (avoid evening — can be stimulating)", comingOff: "No taper required", sideEffects: "Nasal irritation; generally well tolerated" },
  },
  "bpc-157-peptide-capsules-pharmaceutical-grade-recovery-without-prescription": {
    categories: ["recovery", "anti_inflammatory"], injectable: false, caution: [],
    dosing: { dose: "250–500 mcg per capsule", frequency: "1–2x daily", administration: "Oral capsule", bestTime: "On an empty stomach", comingOff: "Typically run 4–8 week cycles", sideEffects: "Generally well tolerated" },
  },
  "nutrafol-dht-inhibitor-md-1": {
    categories: ["skin_hair", "hair_loss"], injectable: false, caution: [],
    dosing: { dose: "As directed on label", frequency: "Daily", administration: "Oral", bestTime: "With food", comingOff: "No taper required", sideEffects: "Generally well tolerated" },
  },
  "testosterone-hormone-and-fertility-booster": {
    categories: ["libido"], injectable: false, caution: [], requiresVisit: true,
    dosing: { dose: "Men's and women's strengths — set by the provider at your visit", frequency: "Daily", administration: "Oral capsule", bestTime: "Same time each morning", comingOff: "Provider-managed — don't stop or restart without guidance", sideEffects: "Generally well tolerated; mood or visual changes warrant a call to the office" },
  },
  "aod-9604": {
    categories: ["fat_loss"], injectable: true, caution: [],
    dosing: { dose: "250–500 mcg per injection", frequency: "Daily", administration: "Subcutaneous injection", bestTime: "Morning, fasted", comingOff: "No taper required", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
  // Epitalon — added 2026-08-11 (was live in the catalog since 8/6 with no
  // quiz entry). Dosing from the Peptide Playbook snapshot. cancer_history
  // caution is a conservative default (telomerase activator) — Derek's call
  // to relax it.
  "epitalon": {
    categories: ["anti_aging", "cognition"], injectable: true, caution: ["cancer_history"],
    dosing: { dose: "~1 mg per injection", frequency: "Daily — one vial is about a 7-week course; take a break between courses", administration: "Subcutaneous injection", bestTime: "Evening/before bed (supports sleep–wake rhythm)", comingOff: "Cycle-based — finish the course and stop; no taper needed", sideEffects: "Injection-site irritation; generally well tolerated" },
  },
};

(function () {
  // -----------------------------
  // Tiny utilities
  // -----------------------------
  function qs(sel, root = document) { return root.querySelector(sel); }
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
  function round(n) { return Math.round(n); }

  // -----------------------------
  // WL session cache (modal-to-modal)
  // -----------------------------
  window.WL = window.WL || {
    sex: "female",
    bmr: null,
    tdee: null,
    // The BMR inputs behind the calorie number. Carried so the macro save can
    // record WHAT the target was computed from — a plan built on a weight
    // 20 lb ago is the usual reason one stops fitting, and that is invisible
    // from the calorie figure alone.
    inputs: { age: null, heightIn: null, weightLb: null, activity: null },
    calories: { maintain: null, slow: null, fast: null, chosen: null, chosenPlan: null }
  };

  // -----------------------------
  // Modal controller
  // -----------------------------
  function openModalByName(name) {
    const modal = qs(`[data-wl-modal="${name}"]`);
    if (!modal) return;

    modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("wl-modal-open");

    // Modal-specific refresh hooks
    if (name === "bmi") {
      renderBmiLog();
      wlLoadBmiGraph();
    }
    if (name === "macros") {
      refreshMacroCaloriesUI();
      // Auto-advance: if a calorie target is already chosen (e.g. carried
      // over from BMR), show the macro breakdown immediately instead of
      // making them click "Calculate targets" again.
      if (window.WL && window.WL.calories && window.WL.calories.chosen) {
        wlComputeAndRenderMacros();
      }
    }
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("wl-modal-open");

    // reset per-modal status lines (nice UX)
    qsa("[data-wl-bmi-status],[data-wl-bmr-status],[data-wl-macro-status]", modal)
      .forEach(el => (el.textContent = ""));
  }

  function initModals() {
    // Open buttons
    qsa("[data-wl-open-modal]").forEach(btn => {
      btn.addEventListener("click", () => {
        const name = btn.getAttribute("data-wl-open-modal");
        if (name) openModalByName(name);
      });
    });

    // Close buttons/backdrops
    qsa("[data-wl-modal]").forEach(modal => {
      qsa("[data-wl-modal-close]", modal).forEach(btn => {
        btn.addEventListener("click", () => closeModal(modal));
      });
    });

    // ESC closes the topmost visible wl modal (simple version)
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      const open = qsa('[data-wl-modal][aria-hidden="false"]');
      if (open.length) closeModal(open[open.length - 1]);
    });
  }

  // -----------------------------
  // BMI (calculator + weekly log scaffold)
  // Storage: localStorage now. Upgrade path later: replace adapter.
  // -----------------------------
  const BMI_LOG_KEY = "wl_bmi_log_v1";

  function storageLoadBmiLog() {
    try {
      const raw = localStorage.getItem(BMI_LOG_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (_) {
      return [];
    }
  }

  function storageSaveBmiLog(entries) {
    try {
      localStorage.setItem(BMI_LOG_KEY, JSON.stringify(entries));
    } catch (_) {
      // ignore
    }
  }

  function todayISO() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function bmiFromImperial(weightLb, heightIn) {
    // BMI = 703 * (lb / in^2)
    if (!weightLb || !heightIn) return null;
    if (heightIn <= 0) return null;
    return 703 * (weightLb / (heightIn * heightIn));
  }

  function bmiCategory(bmi) {
    if (bmi == null || isNaN(bmi)) return "—";
    if (bmi >= 30) return "Obesity (≥ 30)";
    if (bmi >= 25) return "Overweight (25–29.9)";
    if (bmi >= 18.5) return "Normal (18.5–24.9)";
    return "Underweight (< 18.5)";
  }

  function getBmiInputs() {
    const feetEl = qs("[data-wl-bmi-feet]");
    const inchesEl = qs("[data-wl-bmi-inches]");
    const weightEl = qs("[data-wl-bmi-weight]");
    const statusEl = qs("[data-wl-bmi-status]");

    const feet = feetEl ? parseFloat(feetEl.value) : NaN;
    const inches = inchesEl ? parseFloat(inchesEl.value) : NaN;
    const weightLb = weightEl ? parseFloat(weightEl.value) : NaN;

    const heightIn = (feet * 12) + inches;

    return { feet, inches, heightIn, weightLb, statusEl };
  }

  function renderBmiResult(bmi) {
    const valueEl = qs("[data-wl-bmi-value]");
    const catEl = qs("[data-wl-bmi-category]");
    if (valueEl) valueEl.textContent = bmi == null ? "—" : (Math.round(bmi * 10) / 10).toFixed(1);
    if (catEl) catEl.textContent = bmi == null ? "—" : bmiCategory(bmi);
  }

  function renderBmiLog() {
    const listEl = qs("[data-wl-bmi-log-list]");
    if (!listEl) return;

    const entries = storageLoadBmiLog()
      .slice()
      .sort((a, b) => (a.date < b.date ? 1 : -1)); // newest first

    if (!entries.length) {
      listEl.innerHTML = `<div class="wl-help" style="margin-top:0;">No entries yet. Log once per week.</div>`;
      return;
    }

    const rows = entries.slice(0, 12).map(e => {
      const w = (typeof e.weight_lb === "number") ? e.weight_lb.toFixed(1) : "—";
      const b = (typeof e.bmi === "number") ? e.bmi.toFixed(1) : "—";
      return `
        <div style="display:flex; justify-content:space-between; gap:10px; padding:6px 0; border-bottom:1px solid #eee;">
          <div><strong>${e.date || "—"}</strong></div>
          <div>${w} lb</div>
          <div>BMI ${b}</div>
        </div>
      `;
    }).join("");

    listEl.innerHTML = rows + (entries.length > 12 ? `<div class="wl-help">Showing latest 12 entries.</div>` : "");
  }

  // -----------------------------
  // BMI trend graph (server-backed — reads the full history from the
  // customer metafield wl-hub-portal writes to, not just this browser's
  // localStorage) + "not losing at the expected rate" staff-chat prompt.
  // Expected rate (1.2-1.6 lb/week) matches the FAQ copy on the weight
  // loss enrollment page.
  // -----------------------------
  const WL_EXPECTED_MIN_WEEKLY_LOSS = 1.0; // lb/week — below this, flag it

  // Locked goal weight (set once via the check-in; staff-only reset).
  // Cached module-wide so every chart/progress render can use it.
  let wlGoalWeight = null; // { goalLb, setAt } | null
  let wlGoalKnown = false; // true once the server has answered at least once

  let wlChartMode = "weight"; // "weight" | "waist" - toggled by the pills
  let wlLastLog = null;       // last rendered log, so the toggle can re-render

  function wlRenderBmiChart(log) {
    const container = qs("[data-wl-bmi-graph-container]");
    if (!container) return;
    wlLastLog = log || [];

    // Show the Weight/Waist toggle once any waist measurement exists.
    const toggle = qs("[data-wl-chart-toggle]");
    const hasWaist = (log || []).some((e) => Number.isFinite(e.waistIn));
    if (toggle) toggle.style.display = hasWaist ? "flex" : "none";

    const series = wlChartMode === "waist"
      ? (log || []).filter((e) => Number.isFinite(e.waistIn)).map((e) => ({ date: e.date, v: e.waistIn }))
      : (log || []).map((e) => ({ date: e.date, v: e.weightLb }));

    if (series.length < 2) {
      container.innerHTML = wlChartMode === "waist"
        ? `<div class="wl-help" style="margin-top:0;">Log your waist on at least two check-ins to see this trend.</div>`
        : `<div class="wl-help" style="margin-top:0;">Log at least two weeks to see your trend.</div>`;
      return;
    }

    const width = 600, height = 180, padL = 44, padR = 16, padT = 16, padB = 28;
    const weights = series.map((e) => e.v);
    // Pull the goal into the plot range so the goal line is always visible.
    if (wlChartMode === "weight" && wlGoalWeight && Number.isFinite(wlGoalWeight.goalLb)) weights.push(wlGoalWeight.goalLb);
    const minW = Math.min(...weights);
    const maxW = Math.max(...weights);
    const range = Math.max(maxW - minW, 1); // avoid divide-by-zero on flat lines
    const padRange = range * 0.15;
    const yMin = minW - padRange;
    const yMax = maxW + padRange;

    const plotW = width - padL - padR;
    const plotH = height - padT - padB;

    const xFor = (i) => padL + (series.length === 1 ? 0 : (i / (series.length - 1)) * plotW);
    const yFor = (w) => padT + plotH - ((w - yMin) / (yMax - yMin)) * plotH;

    const points = series.map((e, i) => `${xFor(i).toFixed(1)},${yFor(e.v).toFixed(1)}`).join(" ");
    const dots = series
      .map((e, i) => `<circle cx="${xFor(i).toFixed(1)}" cy="${yFor(e.v).toFixed(1)}" r="3.5" fill="#122a44"></circle>`)
      .join("");

    const firstLabel = series[0].date;
    const lastLabel = series[series.length - 1].date;

    // Goal line — dashed brass, labeled, drawn under the trend line.
    let goalLine = "";
    if (wlChartMode === "weight" && wlGoalWeight && Number.isFinite(wlGoalWeight.goalLb)) {
      const gy = yFor(wlGoalWeight.goalLb).toFixed(1);
      goalLine =
        `<line x1="${padL}" y1="${gy}" x2="${width - padR}" y2="${gy}" stroke="#ad8952" stroke-width="1.5" stroke-dasharray="6 4"></line>` +
        `<text x="${width - padR}" y="${(gy - 5)}" font-size="11" fill="#ad8952" font-weight="bold" text-anchor="end">GOAL ${wlGoalWeight.goalLb}</text>`;
    }

    container.innerHTML = `
      <svg viewBox="0 0 ${width} ${height}" style="width:100%;height:auto;display:block;">
        <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + plotH}" stroke="#e5e7eb" stroke-width="1"></line>
        <line x1="${padL}" y1="${padT + plotH}" x2="${width - padR}" y2="${padT + plotH}" stroke="#e5e7eb" stroke-width="1"></line>
        <text x="4" y="${yFor(maxW).toFixed(1)}" font-size="11" fill="#6b7280">${maxW.toFixed(0)}</text>
        <text x="4" y="${(yFor(minW) + 4).toFixed(1)}" font-size="11" fill="#6b7280">${minW.toFixed(0)}</text>
        ${goalLine}
        <polyline points="${points}" fill="none" stroke="#122a44" stroke-width="2.5"></polyline>
        ${dots}
        <text x="${padL}" y="${height - 6}" font-size="11" fill="#6b7280">${firstLabel}</text>
        <text x="${width - padR}" y="${height - 6}" font-size="11" fill="#6b7280" text-anchor="end">${lastLabel}</text>
      </svg>
    `;
  }

  // "Inches lost" summary - first vs latest reading per body part.
  function wlRenderInchesSummary(log) {
    const el = qs("[data-wl-inches-summary]");
    if (!el) return;
    const parts = [["waistIn", "waist"], ["hipsIn", "hips"], ["armsIn", "arms"], ["legsIn", "legs"]];
    const bits = [];
    for (const [key, label] of parts) {
      const vals = (log || []).filter((e) => Number.isFinite(e[key]));
      if (vals.length >= 2) {
        const diff = Math.round((vals[0][key] - vals[vals.length - 1][key]) * 10) / 10;
        if (diff > 0) bits.push("<strong>" + label + " −" + diff + '"</strong>');
        else if (diff < 0) bits.push(label + " +" + Math.abs(diff) + '"');
        else bits.push(label + " even");
      }
    }
    if (!bits.length) { el.style.display = "none"; return; }
    el.style.display = "block";
    el.innerHTML = "📏 Since you started measuring: " + bits.join(" · ") +
      ' <span style="color:#8a8f99;">— inches tell the story even when the scale stalls.</span>';
  }

  // Weight/Waist chart toggle (pills live in the thanks screen).
  document.addEventListener("click", function (e) {
    const pill = e.target.closest("[data-wl-chart-mode]");
    if (!pill) return;
    wlChartMode = pill.getAttribute("data-wl-chart-mode") === "waist" ? "waist" : "weight";
    qsa("[data-wl-chart-mode]").forEach(function (b) {
      b.classList.toggle("is-active", b === pill);
    });
    if (wlLastLog) wlRenderBmiChart(wlLastLog);
  });

  // Confetti burst for goal-reached (Derek: "shoots off some confetti and
  // all that kind of shit"). Dependency-free canvas, brass/navy/cream,
  // fires ONCE per goal value (localStorage) so week two after the win
  // is calm; a staff reset to a new goal re-arms it. Honors
  // prefers-reduced-motion.
  const WL_GOAL_CELEBRATED_KEY = "wl_goal_celebrated_v1";

  function wlConfetti() {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:99999;";
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    const colors = ["#ad8952", "#d4b483", "#122a44", "#ffffff", "#1e7a4d"];
    const parts = [];
    for (let i = 0; i < 160; i++) {
      parts.push({
        x: canvas.width / 2 + (Math.random() - 0.5) * 120,
        y: canvas.height * 0.35,
        vx: (Math.random() - 0.5) * 14,
        vy: -6 - Math.random() * 9,
        w: 5 + Math.random() * 6,
        h: 8 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.3,
        color: colors[i % colors.length]
      });
    }
    const start = Date.now();
    (function tick() {
      const t = Date.now() - start;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      parts.forEach(function (pt) {
        pt.x += pt.vx; pt.vy += 0.25; pt.y += pt.vy; pt.rot += pt.vr;
        ctx.save();
        ctx.translate(pt.x, pt.y);
        ctx.rotate(pt.rot);
        ctx.globalAlpha = Math.max(0, 1 - t / 3000);
        ctx.fillStyle = pt.color;
        ctx.fillRect(-pt.w / 2, -pt.h / 2, pt.w, pt.h);
        ctx.restore();
      });
      if (t < 3200) requestAnimationFrame(tick);
      else canvas.remove();
    })();
  }

  function wlMaybeCelebrateGoal(goalLb) {
    try {
      if (localStorage.getItem(WL_GOAL_CELEBRATED_KEY) === String(goalLb)) return;
      localStorage.setItem(WL_GOAL_CELEBRATED_KEY, String(goalLb));
    } catch (_) {}
    wlConfetti();
  }

  // Progress-to-goal strip: lbs down / to go / % there, plus a pace-based
  // ETA ("on pace to reach it around <date>") derived from the recent trend.
  function wlRenderGoalProgress(log) {
    const el = qs("[data-wl-goal-progress]");
    if (!el) return;
    if (!wlGoalWeight || !Number.isFinite(wlGoalWeight.goalLb) || !log || !log.length) {
      el.style.display = "none";
      return;
    }
    const goal = wlGoalWeight.goalLb;
    const start = log[0].weightLb;
    const current = log[log.length - 1].weightLb;
    const lost = Math.round((start - current) * 10) / 10;
    const toGo = Math.round((current - goal) * 10) / 10;

    let pctHtml = "";
    if (start > goal) {
      const pct = clamp(Math.round(((start - current) / (start - goal)) * 100), 0, 100);
      pctHtml =
        '<div style="height:8px;background:#eef0f4;margin:8px 0 4px;"><div style="height:100%;width:' + pct + '%;background:#ad8952;"></div></div>' +
        '<div style="font-size:15px;color:#6b7280;">' + pct + "% of the way to your goal</div>";
    }

    let headline, sub = "";
    if (toGo <= 0) {
      headline = '<span style="color:#1e7a4d;">🎉 GOAL REACHED — ' + Math.abs(toGo) + " lb past it</span>";
      sub = "Incredible. Talk to us about your maintenance plan — or lock in your next goal and keep rolling.";
      wlMaybeCelebrateGoal(goal);
    } else {
      headline = lost > 0
        ? "<strong>" + lost + " lb down</strong> · " + toGo + " lb to go"
        : toGo + " lb to your goal";
      // Pace over the recent window (up to last 5 entries, needs >= 7 days span).
      const win = log.slice(-5);
      const days = (new Date(win[win.length - 1].date) - new Date(win[0].date)) / 86400000;
      if (win.length >= 2 && days >= 7) {
        const rate = (win[0].weightLb - win[win.length - 1].weightLb) / (days / 7); // lb per week
        if (rate >= 0.15) {
          const weeks = toGo / rate;
          if (weeks <= 104) {
            const eta = new Date(Date.now() + weeks * 7 * 86400000);
            sub = "At your current pace (" + (Math.round(rate * 10) / 10) + " lb/week), you're on track to reach it around <strong>" +
              eta.toLocaleDateString(undefined, { month: "long", year: "numeric" }) + "</strong>.";
          } else {
            sub = "Keep stacking weeks — every entry sharpens this estimate.";
          }
        } else {
          sub = "The scale's been quiet lately — plateaus are normal. A few more weeks of data will put a date on this.";
        }
      } else {
        sub = "A few more weekly entries and we'll show your projected date.";
      }
    }

    el.style.display = "block";
    el.innerHTML =
      '<div style="padding:12px 14px;border:1px solid rgba(173,137,82,.45);border-left:4px solid #ad8952;background:#fbf7f1;">' +
        '<div style="font-size:15px;letter-spacing:.1em;text-transform:uppercase;color:#7a6039;font-weight:700;">Progress to goal · ' + goal + " lb</div>" +
        '<div style="font-size:16px;color:#122a44;margin-top:4px;">' + headline + "</div>" +
        pctHtml +
        (sub ? '<div style="font-size:15px;color:#4b5563;margin-top:6px;line-height:1.5;">' + sub + "</div>" : "") +
      "</div>";
  }

  function wlCheckBmiRate(log) {
    const alertEl = qs("[data-wl-bmi-rate-alert]");
    const messageEl = qs("[data-wl-bmi-rate-message]");
    if (!alertEl || !messageEl) return;

    if (!log || log.length < 2) {
      alertEl.style.display = "none";
      return;
    }

    const latest = log[log.length - 1];
    const prior = log[log.length - 2];

    const days = (new Date(latest.date) - new Date(prior.date)) / (1000 * 60 * 60 * 24);
    if (!isFinite(days) || days <= 0) {
      alertEl.style.display = "none";
      return;
    }

    const lbLost = prior.weightLb - latest.weightLb;
    const weeklyRate = lbLost / (days / 7);

    if (weeklyRate < WL_EXPECTED_MIN_WEEKLY_LOSS) {
      const phrase = weeklyRate < 0
        ? "your weight has gone up since your last entry"
        : "you're losing slower than the typical 1.2–1.6 lb/week";
      messageEl.textContent = `Looks like ${phrase}.`;
      alertEl.style.display = "block";
    } else {
      alertEl.style.display = "none";
    }
  }

  async function wlLoadBmiGraph() {
    const statusEl = qs("[data-wl-bmi-graph-status]");
    if (statusEl) statusEl.textContent = "Loading your history…";

    try {
      const res = await fetch("/apps/wl-hub/bmi");
      const data = await res.json().catch(() => ({}));

      if (!(res.ok && data.ok)) {
        if (statusEl) statusEl.textContent = data.error || "Could not load your history.";
        wlRenderBmiChart([]);
        return;
      }

      if (statusEl) statusEl.textContent = "";
      if (data.goal !== undefined) { wlGoalWeight = data.goal; wlGoalKnown = true; }
      wlRenderBmiChart(data.log || []);
      wlCheckBmiRate(data.log || []);
      wlRenderGoalProgress(data.log || []);
      wlRenderInchesSummary(data.log || []);
      wlRenderReward(data.log || [], data.reward);
    } catch (err) {
      console.error("[WL] Failed to load BMI graph:", err);
      if (statusEl) statusEl.textContent = "Could not load your history.";
    }
  }

  /* ---- Free follow-up visit reward (Derek 2026-08-21) ------------------
     8 distinct ISO check-in weeks inside the rolling last 12 weeks earns a
     single-use 100% code for the follow-up visit. The CRON decides and
     mints (wl-visit-reward.mjs); this only mirrors: progress from the same
     log the trend chart draws, the code from the /apps/wl-hub/bmi
     response's `reward` object. Week math matches the server exactly. */
  const WL_REWARD_NEED = 8;
  const WL_REWARD_WINDOW_DAYS = 84;

  function wlIsoWeek(d) {
    const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const day = t.getUTCDay() || 7;
    t.setUTCDate(t.getUTCDate() + 4 - day);
    const y = t.getUTCFullYear();
    const week = Math.ceil((((t - Date.UTC(y, 0, 1)) / 86400e3) + 1) / 7);
    return y + "-W" + String(week).padStart(2, "0");
  }

  function wlRewardWeeks(log, watermark) {
    const now = Date.now();
    const cutoff = now - WL_REWARD_WINDOW_DAYS * 86400e3;
    const mark = watermark ? new Date(watermark).getTime() : 0;
    const weeks = new Set();
    (log || []).forEach(function (e) {
      const d = new Date(e.date || e.d || 0);
      if (isNaN(d)) return;
      const t = d.getTime();
      if (t >= cutoff && t > mark && t <= now + 86400e3) weeks.add(wlIsoWeek(d));
    });
    return weeks.size;
  }

  function wlRenderReward(log, reward) {
    const card = qs("[data-wl-reward-card]");
    if (!card) return;
    const badge = qs("[data-wl-reward-badge]");
    const title = qs("[data-wl-reward-title]");
    const text = qs("[data-wl-reward-text]");
    const barwrap = qs("[data-wl-reward-barwrap]");
    const bar = qs("[data-wl-reward-bar]");
    const count = qs("[data-wl-reward-count]");
    const codeWrap = qs("[data-wl-reward-code-wrap]");
    const codeEl = qs("[data-wl-reward-code]");

    if (reward && reward.code) {
      /* Unlocked: the cron minted their code. */
      if (badge) badge.style.display = "";
      if (title) title.textContent = "Your free follow-up visit is unlocked";
      if (text) text.textContent = "You showed up week after week - this one's on us. The code is yours alone and works once.";
      if (barwrap) barwrap.style.display = "none";
      if (codeWrap) codeWrap.style.display = "";
      if (codeEl) codeEl.textContent = reward.code;
      const copyBtn = qs("[data-wl-reward-copy]");
      if (copyBtn && !copyBtn.dataset.wlBound) {
        copyBtn.dataset.wlBound = "1";
        copyBtn.addEventListener("click", function () {
          const v = reward.code;
          const done = function () { copyBtn.textContent = "Copied!"; setTimeout(function () { copyBtn.textContent = "Copy"; }, 1600); };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(v).then(done, function () { window.prompt("Copy your code:", v); });
          } else { window.prompt("Copy your code:", v); }
        });
      }
    } else {
      const weeks = wlRewardWeeks(log, reward && reward.watermark);
      const pct = Math.min(100, Math.round((weeks / WL_REWARD_NEED) * 100));
      if (badge) badge.style.display = "none";
      if (title) title.textContent = (reward && reward.redeemedCount) ? "Earn your next free follow-up visit" : "Earn a free follow-up visit";
      if (text) text.textContent = "Check in at least 8 of the last 12 weeks and your next follow-up visit is on us.";
      if (barwrap) barwrap.style.display = "";
      if (bar) bar.style.width = pct + "%";
      if (count) count.textContent = weeks >= WL_REWARD_NEED
        ? "You've hit " + weeks + " weeks - your code is being prepared, check back tomorrow."
        : weeks + " of " + WL_REWARD_NEED + " check-in weeks in the last 12" + (weeks > 0 ? " - keep it going!" : "");
      if (codeWrap) codeWrap.style.display = "none";
    }
    card.style.display = "";
  }

  function initBmi() {
    const calcBtn = qs("[data-wl-bmi-calc]");
    const logBtn = qs("[data-wl-bmi-log]");
    if (!calcBtn && !logBtn) return;

    if (calcBtn) {
      calcBtn.addEventListener("click", () => {
        const { heightIn, weightLb, statusEl } = getBmiInputs();
        if (!isFinite(heightIn) || !isFinite(weightLb) || heightIn <= 0 || weightLb <= 0) {
          if (statusEl) statusEl.textContent = "Please enter a valid height and weight.";
          renderBmiResult(null);
          return;
        }
        const bmi = bmiFromImperial(weightLb, heightIn);
        renderBmiResult(bmi);
        if (statusEl) statusEl.textContent = "Calculated.";
      });
    }

    if (logBtn) {
      logBtn.addEventListener("click", async () => {
        const { heightIn, weightLb, statusEl } = getBmiInputs();
        if (!isFinite(heightIn) || !isFinite(weightLb) || heightIn <= 0 || weightLb <= 0) {
          if (statusEl) statusEl.textContent = "Please enter a valid height and weight before logging.";
          return;
        }

        const bmi = bmiFromImperial(weightLb, heightIn);
        const entry = {
          date: todayISO(),
          weight_lb: Math.round(weightLb * 10) / 10,
          bmi: Math.round(bmi * 10) / 10
        };

        const entries = storageLoadBmiLog();

        // Enforce "once per week" gently: if an entry exists today, overwrite it.
        const existingIdx = entries.findIndex(e => e.date === entry.date);
        if (existingIdx >= 0) entries[existingIdx] = entry;
        else entries.push(entry);

        storageSaveBmiLog(entries);
        renderBmiResult(bmi);
        renderBmiLog();

        if (statusEl) statusEl.textContent = "Logged for this week. (We recommend weighing once per week.)";

        // Persist server-side too (customer note + metafield log) so it's
        // visible to staff and survives a device switch, not just localStorage.
        try {
          const res = await fetch("/apps/wl-hub/bmi", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ date: entry.date, weightLb: entry.weight_lb, bmi: entry.bmi }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.ok) {
            wlRenderBmiChart(data.log || []);
            wlCheckBmiRate(data.log || []);
          } else if (statusEl) {
            statusEl.textContent = "Logged locally, but couldn't save to your profile — please try again later.";
          }
        } catch (err) {
          console.error("[WL] BMI server log failed:", err);
          if (statusEl) statusEl.textContent = "Logged locally, but couldn't save to your profile — please try again later.";
        }
      });
    }
  }

  // -----------------------------
  // BMR + Calorie Plan
  // -----------------------------
  function mifflinBmrKcal(sex, ageYears, heightCm, weightKg) {
    // Mifflin-St Jeor:
    // Male: 10W + 6.25H - 5A + 5
    // Female: 10W + 6.25H - 5A - 161
    const base = (10 * weightKg) + (6.25 * heightCm) - (5 * ageYears);
    return sex === "male" ? (base + 5) : (base - 161);
  }

  function getBmrInputs() {
    const sexEls = qsa('[data-wl-bmr-sex]');
    const ageEl = qs("[data-wl-bmr-age]");
    const feetEl = qs("[data-wl-bmr-feet]");
    const inchesEl = qs("[data-wl-bmr-inches]");
    const weightEl = qs("[data-wl-bmr-weight]");
    const actEl = qs("[data-wl-bmr-activity]");
    const statusEl = qs("[data-wl-bmr-status]");

    let sex = "female";
    sexEls.forEach(r => { if (r.checked) sex = r.value; });

    const age = ageEl ? parseFloat(ageEl.value) : NaN;
    const feet = feetEl ? parseFloat(feetEl.value) : NaN;
    const inches = inchesEl ? parseFloat(inchesEl.value) : NaN;
    const weightLb = weightEl ? parseFloat(weightEl.value) : NaN;
    const activity = actEl ? parseFloat(actEl.value) : 1.2;

    const heightIn = (feet * 12) + inches;

    return { sex, age, feet, inches, heightIn, weightLb, activity, statusEl };
  }

  function setBmrOutputs({ maintain, slow, fast }) {
    const mEl = qs("[data-wl-cal-maintain]");
    const sEl = qs("[data-wl-cal-slow]");
    const fEl = qs("[data-wl-cal-fast]");
    if (mEl) mEl.textContent = maintain != null ? String(maintain) : "—";
    if (sEl) sEl.textContent = slow != null ? String(slow) : "—";
    if (fEl) fEl.textContent = fast != null ? String(fast) : "—";
  }

  function applySafetyMinimum(sex, calories) {
    // Locked by Derek:
    // Female min 1200, Male min 1800
    const floor = (sex === "male") ? 1800 : 1200;
    return Math.max(floor, calories);
  }

  function initBmr() {
    const calcBtn = qs("[data-wl-bmr-calc]");
    if (!calcBtn) return;

    calcBtn.addEventListener("click", () => {
      const { sex, age, heightIn, weightLb, activity, statusEl } = getBmrInputs();

      if (!isFinite(age) || age < 15 || age > 90) {
        if (statusEl) statusEl.textContent = "Please enter a valid age (15–90).";
        return;
      }
      if (!isFinite(heightIn) || heightIn <= 0) {
        if (statusEl) statusEl.textContent = "Please enter a valid height.";
        return;
      }
      if (!isFinite(weightLb) || weightLb <= 0) {
        if (statusEl) statusEl.textContent = "Please enter a valid weight.";
        return;
      }

      // Convert units
      const heightCm = heightIn * 2.54;
      const weightKg = weightLb * 0.453592;

      const bmr = mifflinBmrKcal(sex, age, heightCm, weightKg);
      const tdee = bmr * (isFinite(activity) ? activity : 1.2);

      // Plans
      const maintain = applySafetyMinimum(sex, round(tdee));
      const slow = applySafetyMinimum(sex, round(tdee - 500));
      const fast = applySafetyMinimum(sex, round(tdee - 1000));

      // Save session
      window.WL.sex = sex;
      window.WL.bmr = round(bmr);
      window.WL.tdee = round(tdee);
      window.WL.inputs = { age: age, heightIn: heightIn, weightLb: weightLb, activity: activity };
      window.WL.calories.maintain = maintain;
      window.WL.calories.slow = slow;
      window.WL.calories.fast = fast;

      setBmrOutputs({ maintain, slow, fast });

      if (statusEl) statusEl.textContent = "Plan calculated. Choose Maintain / Slow / Fast and click “Use”.";
    });

    // "Use" buttons: store chosen calories, then auto-advance straight
    // into Macro Targets with the breakdown already calculated — no
    // manual "now go open Macros yourself" step.
    qsa("[data-wl-use-calories]").forEach(btn => {
      btn.addEventListener("click", () => {
        const key = btn.getAttribute("data-wl-use-calories"); // maintain|slow|fast
        const chosen = window.WL && window.WL.calories ? window.WL.calories[key] : null;

        const statusEl = qs("[data-wl-bmr-status]");
        if (!chosen) {
          if (statusEl) statusEl.textContent = "Please calculate your plan first.";
          return;
        }

        window.WL.calories.chosen = chosen;
        window.WL.calories.chosenPlan = key;

        const bmrModal = btn.closest("[data-wl-modal]");
        if (bmrModal) closeModal(bmrModal);
        openModalByName("macros");
      });
    });
  }

  // -----------------------------
  // Macro Targets (percent + grams)
  // -----------------------------
  const MACRO_TEMPLATES = {
    balanced:   { protein: 0.30, carbs: 0.40, fat: 0.30 },
    high_protein: { protein: 0.40, carbs: 0.30, fat: 0.30 },
    high_fat:   { protein: 0.30, carbs: 0.20, fat: 0.50 }
  };

  function refreshMacroCaloriesUI() {
    const calEl = qs("[data-wl-macro-calories]");
    if (!calEl) return;
    calEl.textContent = window.WL && window.WL.calories && window.WL.calories.chosen
      ? String(window.WL.calories.chosen)
      : "—";
  }

  function setMacroOutputs({ pPct, cPct, fPct, pG, cG, fG }) {
    const pGEl = qs("[data-wl-macro-protein-g]");
    const cGEl = qs("[data-wl-macro-carbs-g]");
    const fGEl = qs("[data-wl-macro-fat-g]");

    const pPEl = qs("[data-wl-macro-protein-pct]");
    const cPEl = qs("[data-wl-macro-carbs-pct]");
    const fPEl = qs("[data-wl-macro-fat-pct]");

    if (pGEl) pGEl.textContent = pG != null ? String(pG) : "—";
    if (cGEl) cGEl.textContent = cG != null ? String(cG) : "—";
    if (fGEl) fGEl.textContent = fG != null ? String(fG) : "—";

    if (pPEl) pPEl.textContent = pPct != null ? String(pPct) : "—";
    if (cPEl) cPEl.textContent = cPct != null ? String(cPct) : "—";
    if (fPEl) fPEl.textContent = fPct != null ? String(fPct) : "—";
  }

  // Module-scope (not nested in initMacros) so openModalByName and the BMR
  // "Use" handler can both trigger it directly — needed for auto-advance:
  // BMR -> Macros should show computed targets immediately, not require a
  // second manual "Calculate" click.
  function wlComputeAndRenderMacros(statusOverride) {
    const templateEl = qs("[data-wl-macro-template]");
    const statusEl = qs("[data-wl-macro-status]");
    const calories = window.WL && window.WL.calories ? window.WL.calories.chosen : null;

    if (!calories) {
      if (statusEl) statusEl.textContent = "Run BMR + Calorie Plan first, then click “Use” on a calorie target.";
      setMacroOutputs({ pPct: null, cPct: null, fPct: null, pG: null, cG: null, fG: null });
      refreshMacroCaloriesUI();
      return;
    }

    const key = templateEl ? (templateEl.value || "balanced") : "balanced";
    const tpl = MACRO_TEMPLATES[key] || MACRO_TEMPLATES.balanced;

    // Percent output
    const pPct = Math.round(tpl.protein * 100);
    const cPct = Math.round(tpl.carbs * 100);
    const fPct = Math.round(tpl.fat * 100);

    // Gram output:
    // Protein & carbs ~4 kcal/g; fat ~9 kcal/g
    const proteinCals = calories * tpl.protein;
    const carbsCals = calories * tpl.carbs;
    const fatCals = calories * tpl.fat;

    const pG = Math.round(proteinCals / 4);
    const cG = Math.round(carbsCals / 4);
    const fG = Math.round(fatCals / 9);

    setMacroOutputs({ pPct, cPct, fPct, pG, cG, fG });
    refreshMacroCaloriesUI();

    if (statusEl) {
      statusEl.textContent = statusOverride || "Targets calculated. Tip: enter these grams into MyFitnessPal as your daily goals.";
    }

    // Hold the freshly computed targets for the Save button rather than
    // writing them immediately — see wlSaveMacroTargets for why.
    wlPendingMacros = {
      calories: calories, template: key,
      proteinG: pG, carbsG: cG, fatG: fG,
      proteinPct: pPct, carbsPct: cPct, fatPct: fPct
    };
    const saveBtn = qs("[data-wl-macro-save]");
    const savedFlag = qs("[data-wl-macro-saved]");
    if (saveBtn) { saveBtn.style.display = ""; saveBtn.disabled = false; saveBtn.textContent = "Save my targets"; }
    if (savedFlag) savedFlag.style.display = "none";
  }

  // Persist the targets to the customer record (Derek 2026-08-23 — before
  // this the calculator wrote to window.WL and nothing else, so there was no
  // way to know whether a member had ever run it, let alone what they were
  // aiming for).
  //
  // EXPLICIT SAVE, not auto-save (Derek 2026-08-24: "there should be some kind
  // of save button"). The first version wrote on every recompute, which was
  // wrong twice over:
  //   1. It was fire-and-forget and never checked the response, so a 401 from
  //      an expired app-proxy session looked exactly like success. That is the
  //      bug that silently ate a week of weigh-ins in August; the check-in flow
  //      was hardened against it and this path had been left behind.
  //   2. It counted a "run" every time the template dropdown moved. Derek's own
  //      test produced runCount 2 in 1.7 seconds, which made the number useless
  //      as an engagement signal in the outreach brief.
  // Now the member presses Save, we check res.ok AND data.ok, and we say what
  // happened either way.
  let wlPendingMacros = null;

  function wlSaveMacroTargets() {
    if (!wlPendingMacros) return;
    const w = window.WL || {};
    const body = Object.assign({}, wlPendingMacros, {
      plan: (w.calories && w.calories.chosenPlan) || null,
      bmr: w.bmr || null,
      tdee: w.tdee || null,
      sex: w.sex || null,
      age: (w.inputs && w.inputs.age) || null,
      heightIn: (w.inputs && w.inputs.heightIn) || null,
      weightLb: (w.inputs && w.inputs.weightLb) || null,
      activity: (w.inputs && w.inputs.activity) || null
    });

    const btn = qs("[data-wl-macro-save]");
    const savedFlag = qs("[data-wl-macro-saved]");
    const statusEl = qs("[data-wl-macro-status]");
    if (btn) { btn.disabled = true; btn.textContent = "Saving…"; }

    function fail(msg) {
      if (btn) { btn.disabled = false; btn.textContent = "Save my targets"; }
      if (statusEl) {
        statusEl.innerHTML = '<span style="color:#8a1c16;">' +
          "<strong>We couldn't save your targets.</strong> " + msg +
          "</span>";
      }
    }

    fetch("/apps/wl-hub/macros", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok || !data.ok) {
          console.error("[WL] Macro save rejected:", res.status, data);
          fail(res.status === 401
            ? "Please make sure you're signed in to your account, then try again."
            : "Please try again in a moment, or let us know if it keeps happening.");
          return;
        }
        if (btn) btn.style.display = "none";
        if (savedFlag) savedFlag.style.display = "";
        if (statusEl) statusEl.textContent = "Saved. Tip: enter these grams into MyFitnessPal as your daily goals.";
        wlMacrosOnFile = data.macros || wlMacrosOnFile;
        wlRefreshMacroPrompt();
      });
    }).catch(function (err) {
      console.error("[WL] Macro save failed:", err);
      fail("Check your connection and try again.");
    });
  }

  // What the server currently holds, so the check-in's thank-you screen knows
  // whether to nudge. Populated on load and refreshed after a successful save.
  let wlMacrosOnFile = null;

  function wlRefreshMacroPrompt() {
    const prompt = qs("[data-wl-checkin-macro-prompt]");
    if (prompt) prompt.style.display = wlMacrosOnFile ? "none" : "block";
  }

  function initMacroSave() {
    const btn = qs("[data-wl-macro-save]");
    if (btn) btn.addEventListener("click", wlSaveMacroTargets);
    const open = qs("[data-wl-open-macros]");
    if (open) open.addEventListener("click", function () { openModalByName("macros"); });
    fetch("/apps/wl-hub/macros")
      .then(function (r) { return r.json(); })
      .then(function (d) { if (d && d.ok) wlMacrosOnFile = d.macros || null; })
      .catch(function () {})
      .then(function () { wlRefreshMacroPrompt(); });
  }

  function initMacros() {
    const calcBtn = qs("[data-wl-macro-calc]");
    const templateEl = qs("[data-wl-macro-template]");
    if (!calcBtn || !templateEl) return;

    // Button click
    calcBtn.addEventListener("click", () => wlComputeAndRenderMacros());

    // Recompute on template change if calories already chosen
    templateEl.addEventListener("change", () => {
      if (window.WL && window.WL.calories && window.WL.calories.chosen) wlComputeAndRenderMacros();
      else refreshMacroCaloriesUI();
    });

    // Keep calories display fresh
    refreshMacroCaloriesUI();
  }


  // -----------------------------
  // Dose confirmation flow (UI only for now)
  // -----------------------------
  let pendingDoseMg = null;

  function initDose() {
    const doseInput = qs("[data-wl-dose-input]");
    const saveBtn = qs("[data-wl-dose-save]");

    if (saveBtn && doseInput) {
      saveBtn.addEventListener("click", () => {
        const val = parseFloat(doseInput.value);
        const status = qs("[data-wl-dose-status]");

        if (!isFinite(val) || val <= 0) {
          if (status) status.textContent = "Please enter a valid dose in mg.";
          return;
        }

        pendingDoseMg = val;
        openModalByName("dose-confirm");
      });
    }

    const confirmBtn = qs("[data-wl-dose-confirm]");
    if (confirmBtn) {
      confirmBtn.addEventListener("click", async () => {
        if (!pendingDoseMg) return;

        const doseToSave = pendingDoseMg;
        confirmBtn.disabled = true;

        try {
          const res = await fetch("/apps/wl-hub/dose", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ dose: doseToSave }),
          });
          const data = await res.json().catch(() => ({}));

          const status = qs("[data-wl-dose-status]");
          if (res.ok && data.ok) {
            if (status) status.textContent = `Saved — dose on file: ${doseToSave} mg.`;
          } else if (status) {
            status.textContent = data.error || "Could not save your dose — please try again.";
          }
        } catch (err) {
          console.error("[WL] Dose save failed:", err);
          const status = qs("[data-wl-dose-status]");
          if (status) status.textContent = "Could not save your dose — please try again.";
        }

        confirmBtn.disabled = false;
        pendingDoseMg = null;
        closeModal(confirmBtn.closest("[data-wl-modal]"));
      });
    }
  }

  // -----------------------------
  // -----------------------------
  // Dose calculator (units -> weekly mg), ported from the post-visit
  // checkout page's inline dose calc. Assumes standard 100 units = 1 mL
  // insulin syringe, same as the source implementation.
  // -----------------------------
  let wlDosecalcResultMg = null;

  function wlDosecalcSetUseEnabled(enabled) {
    const btn = qs("[data-wl-dosecalc-use]");
    if (!btn) return;
    btn.disabled = !enabled;
    btn.style.opacity = enabled ? "1" : "0.45";
    btn.style.cursor = enabled ? "pointer" : "not-allowed";
  }

  function initDoseCalculator() {
    const computeBtn = qs("[data-wl-dosecalc-compute]");
    if (!computeBtn) return; // modal not on this page

    const concSel = qs("[data-wl-dosecalc-conc]");
    const customWrap = qs("[data-wl-dosecalc-custom-wrap]");
    if (concSel) {
      concSel.addEventListener("change", () => {
        if (customWrap) customWrap.style.display = concSel.value === "__custom__" ? "block" : "none";
      });
    }

    computeBtn.addEventListener("click", () => {
      const unitsEl = qs("[data-wl-dosecalc-units]");
      const customEl = qs("[data-wl-dosecalc-custom-conc]");
      const hintEl = qs("[data-wl-dosecalc-hint]");

      const units = unitsEl ? parseFloat(unitsEl.value) : NaN;
      const sel = concSel ? concSel.value : "";
      const conc = sel === "__custom__" ? parseFloat(customEl?.value) : parseFloat(sel);

      if (!isFinite(units) || units <= 0) {
        if (hintEl) hintEl.textContent = "Please enter a valid number of units (example: 25).";
        wlDosecalcResultMg = null;
        wlDosecalcSetUseEnabled(false);
        return;
      }
      if (!isFinite(conc) || conc <= 0) {
        if (hintEl) hintEl.textContent = "Please select (or enter) a valid concentration (mg/mL).";
        wlDosecalcResultMg = null;
        wlDosecalcSetUseEnabled(false);
        return;
      }

      const ml = units / 100;
      wlDosecalcResultMg = Math.round(ml * conc * 100) / 100;

      const mgEl = qs("[data-wl-dosecalc-result-mg]");
      // mg | mcg display toggle (Derek 2026-08-28). The LOGGED dose stays in
      // mg - this only changes what the reader sees on the result line.
      (function ensureDoseUnitToggle() {
        if (!mgEl || mgEl.getAttribute("data-unit-toggle")) return;
        mgEl.setAttribute("data-unit-toggle", "1");
        const line = mgEl.parentElement;
        const w = document.createElement("span");
        w.style.cssText = "margin-left:10px;font-size:12.5px;";
        w.innerHTML = '<button type="button" data-wl-dosecalc-unit="mg" style="border:1px solid #122a44;background:#122a44;color:#fff;padding:2px 9px;cursor:pointer;font:inherit;border-radius:0;">mg</button>' +
          '<button type="button" data-wl-dosecalc-unit="mcg" style="border:1px solid #122a44;background:#fff;color:#122a44;padding:2px 9px;cursor:pointer;font:inherit;border-radius:0;">mcg</button>';
        if (line) line.appendChild(w);
        w.addEventListener("click", function (e) {
          const b = e.target.closest("[data-wl-dosecalc-unit]");
          if (!b) return;
          const unit = b.getAttribute("data-wl-dosecalc-unit");
          w.querySelectorAll("[data-wl-dosecalc-unit]").forEach(function (x) {
            const on = x === b;
            x.style.background = on ? "#122a44" : "#fff";
            x.style.color = on ? "#fff" : "#122a44";
          });
          const mg = parseFloat(mgEl.getAttribute("data-mg") || mgEl.textContent);
          if (!isFinite(mg)) return;
          if (unit === "mcg") {
            mgEl.textContent = (Math.round(mg * 1000)).toLocaleString();
            mgEl.nextSibling && (mgEl.nextSibling.textContent = " mcg/week");
          } else {
            mgEl.textContent = mg;
            mgEl.nextSibling && (mgEl.nextSibling.textContent = " mg/week");
          }
        });
      })();
      const labelEl = qs("[data-wl-dosecalc-result-label]");
      if (mgEl) {
        // canonical mg after every compute: stamp it, show it, and snap the
        // display toggle back to mg so number and label can never disagree
        mgEl.setAttribute("data-mg", wlDosecalcResultMg.toFixed(2));
        mgEl.textContent = wlDosecalcResultMg.toFixed(2);
        if (mgEl.nextSibling) mgEl.nextSibling.textContent = " mg/week";
        document.querySelectorAll("[data-wl-dosecalc-unit]").forEach(function (x) {
          const on = x.getAttribute("data-wl-dosecalc-unit") === "mg";
          x.style.background = on ? "#122a44" : "#fff";
          x.style.color = on ? "#fff" : "#122a44";
        });
      }
      if (labelEl) labelEl.textContent = `${units} units/week @ ${conc} mg/mL`;
      if (hintEl) hintEl.textContent = "";

      wlDosecalcSetUseEnabled(true);
    });

    const useBtn = qs("[data-wl-dosecalc-use]");
    if (useBtn) {
      useBtn.addEventListener("click", () => {
        if (!wlDosecalcResultMg) return;
        // Weekly check-in's dose field is the primary target since the hero
        // dose card was folded into it (2026-08-11); the old selector is
        // kept as a fallback for any page still carrying that card.
        const doseInput = qs("[data-wl-checkin-dose]") || qs("[data-wl-dose-input]");
        if (doseInput) doseInput.value = wlDosecalcResultMg;
        closeModal(useBtn.closest("[data-wl-modal]"));
      });
    }
  }

  // -----------------------------
  // Add-ons: product quick-shop modal
  // (same pattern as the aesthetics membership lounge)
  // -----------------------------
  let wlCurrentProduct = null;
  let wlSelectedVariant = null;

  // Opens the theme's native cart drawer (if present) instead of navigating
  // to /cart, so members can keep adding add-ons without leaving the page —
  // same helper as wl-post-visit-checkout.js / the membership lounge.
  function wlTryOpenCartDrawer() {
    const openCartBtn =
      qs('[data-cart-drawer-open]') ||
      qs('[data-drawer-open="cart"]') ||
      qs('button[name="open_cart"]') ||
      qs('a[href="/cart"]') ||
      null;

    if (openCartBtn) openCartBtn.click();
  }

  // Mirror Shopify's REAL prices in the quick-shop. The discount app writes
  // the sale price and sets compare_at_price — NEVER re-discount here (that
  // double-displays the discount and mismatches the cart).
  function wlPriceHtml(cents, compareCents) {
    var price = "$" + (cents / 100).toFixed(2);
    if (!compareCents || compareCents <= cents) return price;
    var full = "$" + (compareCents / 100).toFixed(2);
    var pct = Math.round((1 - cents / compareCents) * 100);
    return '<span style="text-decoration:line-through;color:#9ca3af;font-weight:400;">' + full + "</span> " +
      '<span style="color:#7a6039;font-weight:800;">' + price + "</span> " +
      '<span style="font-size:14px;color:#1f3e63;font-weight:700;">' + pct + "% off</span>";
  }

  function wlRenderProduct(product) {
    wlCurrentProduct = product;
    wlSelectedVariant = product.variants.length === 1 ? product.variants[0].id : null;

    const mediaEl = qs("[data-wl-product-media]");
    const titleEl = qs("[data-wl-product-title]");
    const priceEl = qs("[data-wl-product-price]");
    const descEl = qs("[data-wl-product-desc]");
    const variantsEl = qs("[data-wl-product-variants]");
    const statusEl = qs("[data-wl-cart-status]");

    if (titleEl) titleEl.textContent = product.title;
    if (priceEl && product.variants[0]) {
      priceEl.innerHTML = wlPriceHtml(product.variants[0].price, product.variants[0].compare_at_price);
    }
    // full product page link — new tab so the hub session (quiz picks,
    // scroll position) stays alive behind it
    const linkEl = qs("[data-wl-product-link]");
    if (linkEl) linkEl.href = product.url || "/products/" + product.handle;

    if (descEl) {
      // Short selling blurb from the product description — strip HTML, trim.
      var raw = (product.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      if (raw.length > 240) raw = raw.slice(0, 240).replace(/\s+\S*$/, "") + "…";
      descEl.textContent = raw;
      descEl.style.display = raw ? "block" : "none";
    }
    if (statusEl) statusEl.textContent = "";

    if (mediaEl) {
      const img = product.images && product.images[0];
      mediaEl.innerHTML = img
        ? `<img src="${img}" alt="${product.title}" style="width:100%;border-radius:12px;display:block;">`
        : "";
    }

    if (variantsEl) {
      variantsEl.innerHTML = "";
      if (product.variants.length > 1) {
        product.variants.forEach((variant) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = variant.title;
          btn.className = "wl-variant-option";
          btn.style.cssText =
            "padding:6px 12px;border:1px solid #e5e7eb;border-radius:20px;background:#f5f5f5;cursor:pointer;";
          btn.addEventListener("click", () => {
            wlSelectedVariant = variant.id;
            qsa(".wl-variant-option", variantsEl).forEach((b) => (b.style.background = "#f5f5f5"));
            btn.style.background = "#dbeafe";
            if (priceEl) priceEl.innerHTML = wlPriceHtml(variant.price, variant.compare_at_price);
            // swap to the variant's own photo when it has one assigned
            if (mediaEl && variant.featured_image && variant.featured_image.src) {
              mediaEl.innerHTML =
                '<img src="' + variant.featured_image.src + '" alt="' + product.title +
                '" style="width:100%;border-radius:12px;display:block;">';
            }
          });
          variantsEl.appendChild(btn);
        });
      }
    }
  }

  // -----------------------------
  // GHL chat bridge
  // -----------------------------
  // window.leadConnector.chatWidget.openWidget() is GHL's real API for this
  // widget — confirmed via live testing 2026-07-16 (same fix already live in
  // wl-post-visit-checkout.js). Simulating a click on the widget's internal
  // shadow-DOM launcher (the old approach here) looks like it works — finds
  // a real button, clicks it, no error — but the widget silently ignores
  // untrusted synthetic clicks and never opens. Don't reintroduce that
  // fallback: because it returns `true`, the retry loop stops, permanently
  // masking the case where leadConnector just isn't ready yet.
  function wlOpenGhlChat() {
    // Toggle, not just open (2026-07-17). Capture the widget's state
    // SYNCHRONOUSLY, then act after a short delay so the triggering click
    // finishes bubbling first — GHL's own outside-click listener closes
    // the widget when a click lands outside it, which was instantly
    // undoing our open and making buttons look dead.
    var lc = window.leadConnector && window.leadConnector.chatWidget;
    if (!lc || typeof lc.openWidget !== "function") return false;
    var el = document.querySelector("chat-widget");
    var wasActive = false;
    try {
      wasActive = typeof lc.isActive === "function"
        ? !!lc.isActive()
        : !!(el && el.getAttribute("data-active") === "true");
    } catch (e) {}
    setTimeout(function () {
      try {
        if (wasActive && typeof lc.closeWidget === "function") lc.closeWidget();
        else lc.openWidget();
      } catch (e) {}
    }, 60);
    return true;
  }

  // Peptides tagged 'needs-mixing' are unusable without bacteriostatic
  // water, so a Sterile Water vial ($9) rides along automatically —
  // transparently (the status line says so) and only when the cart
  // doesn't already contain one. Variant id from /products/sterile-water.js.
  const WL_STERILE_WATER_VARIANT = 48043160830119;

  async function wlEnsureSterileWater() {
    try {
      const cart = await fetch("/cart.js").then((r) => r.json());
      if (cart.items.some((i) => i.variant_id === WL_STERILE_WATER_VARIANT)) return false;
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: WL_STERILE_WATER_VARIANT, quantity: 1 }),
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  async function wlLoadProduct(handle) {
    try {
      const product = await fetch(`/products/${handle}.js`).then((r) => r.json());
      wlRenderProduct(product);
      openModalByName("product");
    } catch (err) {
      console.error("WL Add-ons: failed to load product", err);
    }
  }

  function initAddons() {
    qsa(".wl-product-trigger").forEach((btn) => {
      btn.addEventListener("click", () => {
        const handle = btn.getAttribute("data-handle");
        if (handle) wlLoadProduct(handle);
      });
    });

    const addToCartBtn = qs("[data-wl-add-to-cart]");
    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", async () => {
        const statusEl = qs("[data-wl-cart-status]");
        if (!wlSelectedVariant) {
          if (statusEl) statusEl.textContent = "Please select an option first.";
          return;
        }

        if (statusEl) statusEl.textContent = "Adding…";
        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: wlSelectedVariant, quantity: 1 }),
          });
          if (!res.ok) {
            if (statusEl) statusEl.textContent = "Something went wrong — please try again.";
          } else {
            let msg = "Added to cart! You can keep shopping add-ons below.";
            const tags = (wlCurrentProduct && wlCurrentProduct.tags) || [];
            if (tags.indexOf("needs-mixing") > -1) {
              const waterAdded = await wlEnsureSterileWater();
              if (waterAdded) msg = "Added to cart — plus a vial of sterile water for mixing (remove it in the cart if you already have some).";
            }
            if (statusEl) statusEl.textContent = msg;
            setTimeout(wlTryOpenCartDrawer, 250);
          }
        } catch (err) {
          console.error("WL Add-ons: add to cart failed", err);
          if (statusEl) statusEl.textContent = "Something went wrong — please try again.";
        }
      });
    }
  }

  // -----------------------------
  // Peptide selector quiz
  // -----------------------------
  function wlLoadCatalog() {
    const el = qs("#wl-addons-catalog");
    if (!el) return [];
    try {
      const products = JSON.parse(el.textContent);
      // Merge live Shopify data with the editorial goal-tagging above.
      // Products with no tagging entry (e.g. Sterile Water) are dropped —
      // they're catalog items, not something the quiz should ever recommend.
      // Twin alias: graduated members' catalog carries member-twin handles
      // (`<original>-m`, 15% baked into the price) — PEPTIDE_TAGS stays keyed
      // by ORIGINAL handles, so strip the suffix for the lookup only when the
      // stripped form is a real key, and keep the twin handle on the entry so
      // the quick-shop modal loads the member-priced listing.
      const pepBase = (h) => {
        if (!h || PEPTIDE_TAGS[h]) return h;
        const b = h.replace(/-m$/, "");
        return PEPTIDE_TAGS[b] ? b : h;
      };
      return products
        .filter((p) => PEPTIDE_TAGS[pepBase(p.handle)])
        .map((p) => Object.assign({}, p, PEPTIDE_TAGS[pepBase(p.handle)], { handle: p.handle }));
    } catch (err) {
      console.error("WL Peptide Quiz: failed to parse catalog", err);
      return [];
    }
  }

  function wlRenderQuizGoalCheckboxes() {
    const container = qs("[data-wl-quiz-goals]");
    if (!container) return;
    container.innerHTML = "";
    WL_QUIZ_GOALS.forEach((goal) => {
      const label = document.createElement("label");
      label.style.cssText = "display:flex;gap:8px;align-items:center;font-size:16px;";
      label.innerHTML = `<input type="checkbox" data-wl-quiz-goal value="${goal.key}"> ${goal.label}`;
      container.appendChild(label);
    });
  }

  function wlScoreProducts(selectedGoals, noInjectables) {
    const catalog = wlLoadCatalog();
    const ranked = catalog
      .map((product) => {
        const score = product.categories.filter((c) => selectedGoals.includes(c)).length;
        return Object.assign({}, product, { score });
      })
      .filter((product) => product.score > 0)
      .filter((product) => product.available !== false)
      // SOFT RANK, not a hard filter (Derek 2026-08-30: "soft rank it, don't
      // hide the injectables") — needle-free floats to the top for avoiders,
      // injectables stay in the list below.
      .sort((a, b) => {
        if (noInjectables) {
          const an = a.injectable === false ? 0 : 1;
          const bn = b.injectable === false ? 0 : 1;
          if (an !== bn) return an - bn;
        }
        return b.score - a.score;
      });

    // Top picks by overall match — plus GUARANTEED SPECIALIST COVERAGE:
    // every goal the member checked surfaces the product that SPECIALIZES
    // in it (primary category = categories[0]), not just a generalist that
    // lists it as a side benefit. Without this, PT-141 (libido specialist)
    // never shows in batches because MOTS-C "covers" libido incidentally.
    const picks = ranked.slice(0, 4);
    const inPicks = (p) => picks.some((x) => x.handle === p.handle);
    selectedGoals.forEach((goal) => {
      const hasSpecialist = picks.some((p) => p.categories[0] === goal);
      if (!hasSpecialist) {
        const champ = ranked.find((p) => p.categories[0] === goal && !inPicks(p));
        if (champ) { picks.push(champ); return; }
      }
      if (!picks.some((p) => p.categories.includes(goal))) {
        const best = ranked.find((p) => p.categories.includes(goal) && !inPicks(p));
        if (best) picks.push(best);
      }
    });
    return picks;
  }

  function wlRenderQuizResults(matches, { pregnancy, cancer }) {
    const listEl = qs("[data-wl-quiz-results-list]");
    if (!listEl) return;

    if (matches.length === 0) {
      listEl.innerHTML = `<div class="wl-help" style="margin-top:0;">
        No specific matches for that combination — browse everything in the Add-ons section above,
        or ask your provider what they'd recommend for your goals.
      </div>`;
      return;
    }

    listEl.innerHTML = matches
      .map((product) => {
        const goalLabels = product.categories
          .map((c) => (WL_QUIZ_GOALS.find((g) => g.key === c) || {}).label)
          .filter(Boolean)
          .join(", ");

        const cautions = [];
        if (product.caution.includes("pregnancy") && pregnancy !== "no") {
          cautions.push("Please discuss this one with your provider given your pregnancy status.");
        }
        if (product.caution.includes("cancer_history") && cancer !== "no") {
          cautions.push("This acts on growth pathways — please flag your cancer history to your provider first.");
        }

        const d = product.dosing;
        const dosingRows = d
          ? `
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 14px;margin-top:10px;font-size:15px;">
              <div><strong>Dose</strong><br>${d.dose}</div>
              <div><strong>How often</strong><br>${d.frequency}</div>
              <div><strong>Administration</strong><br>${d.administration}</div>
              <div><strong>Best time</strong><br>${d.bestTime}</div>
              <div><strong>Coming off</strong><br>${d.comingOff}</div>
              <div><strong>Possible side effects</strong><br>${d.sideEffects}</div>
            </div>
          `
          : "";

        // Every variant gets its own checkbox row so members can pick
        // exactly what they want across all recommendations and add the
        // whole selection to the cart in one shot (2026-07-16, Derek).
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const variantRows = variants
          .map((v) => {
            const label =
              variants.length === 1 || v.title === "Default Title"
                ? `Add ${product.title}`
                : v.title;
            return v.available === false
              ? `<label style="display:flex;gap:8px;align-items:center;padding:7px 10px;border:1px dashed #e5e7eb;border-radius:10px;color:#9ca3af;font-size:15px;">
                   <input type="checkbox" disabled> ${label} — ${v.price} (sold out)
                 </label>`
              : `<label style="display:flex;gap:8px;align-items:center;padding:7px 10px;border:1px solid #e5e7eb;border-radius:10px;cursor:pointer;font-size:15px;">
                   <input type="checkbox" data-wl-quiz-variant="${v.id}"> <span style="flex:1;">${label}</span> <strong>${v.price}</strong>
                 </label>`;
          })
          .join("");

        return `
          <div style="border:1px solid #e5e7eb;border-radius:14px;padding:14px;">
            <div style="font-weight:700;color:#111827;"><button type="button" data-wl-quiz-info="${product.handle}" style="all:unset;cursor:pointer;font-weight:700;color:#111827;">${product.title} <span style="color:#7a6039;font-weight:700;font-size:15px;">ⓘ More info</span></button></div>
            ${product.injectable === false ? '<div style="display:inline-block;background:#eef6f0;color:#2b5c45;border:1.5px solid rgba(43,92,69,.5);font-weight:700;font-size:16px;padding:2px 10px;border-radius:2px;margin-top:4px;">💧 Needle-free</div>' : ""}
            <div style="color:#6b7280;font-size:16px;margin-top:2px;">Good for: ${goalLabels}</div>
            ${dosingRows}
            ${cautions.length ? `<div style="color:#b45309;font-size:15px;margin-top:10px;">⚠ ${cautions.join(" ")}</div>` : ""}
            ${""/* visit-required banner removed 2026-08-28 (Derek): everyone inside
                this gated portal is an established patient - "we have already
                seen them. That is why they are in there to begin with." The
                public anti-aging surface keeps its banner; only the portals skip it. */}
            <div style="display:grid;gap:6px;margin-top:12px;">${variantRows}</div>
          </div>
        `;
      })
      .join("");

    listEl.innerHTML += `
      <div style="position:sticky;bottom:0;background:#fff;padding-top:10px;">
        <button type="button" class="button button--primary" data-wl-quiz-add-selected disabled
          style="width:100%;opacity:.45;cursor:not-allowed;">
          Add selected to cart (0)
        </button>
        <div data-wl-quiz-cart-status style="color:#6b7280;font-size:15px;margin-top:8px;text-align:center;"></div>
      </div>
    `;

    const addBtn = qs("[data-wl-quiz-add-selected]", listEl);

    function refreshAddButton() {
      const count = qsa("[data-wl-quiz-variant]:checked", listEl).length;
      addBtn.textContent = `Add selected to cart (${count})`;
      addBtn.disabled = count === 0;
      addBtn.style.opacity = count === 0 ? ".45" : "1";
      addBtn.style.cursor = count === 0 ? "not-allowed" : "pointer";
    }
    listEl.addEventListener("change", (e) => {
      if (e.target.closest("[data-wl-quiz-variant]")) refreshAddButton();
    });

    listEl.addEventListener("click", (e) => {
      const infoBtn = e.target.closest("[data-wl-quiz-info]");
      if (!infoBtn) return;
      // deliberately does NOT close the quiz — the product modal stacks
      // on top (z-index bump in wl__core.css); closing it returns the
      // member to their in-progress selections.
      wlLoadProduct(infoBtn.getAttribute("data-wl-quiz-info"));
    });

    addBtn.addEventListener("click", async () => {
      const ids = qsa("[data-wl-quiz-variant]:checked", listEl).map((el) =>
        parseInt(el.getAttribute("data-wl-quiz-variant"), 10)
      );
      if (!ids.length) return;

      const statusEl = qs("[data-wl-quiz-cart-status]", listEl);
      if (statusEl) statusEl.textContent = "Adding…";
      addBtn.disabled = true;

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
        });
        if (res.ok) {
          // if anything they picked needs mixing, ride a sterile water vial along
          const cat = wlLoadCatalog();
          const needsMixing = ids.some((id) =>
            cat.some(
              (p) =>
                (p.tags || []).indexOf("needs-mixing") > -1 &&
                (p.variants || []).some((v) => v.id === id)
            )
          );
          if (needsMixing) await wlEnsureSterileWater();
          if (statusEl) statusEl.textContent = `Added ${ids.length} item${ids.length > 1 ? "s" : ""} to your cart!`;
          closeModal(addBtn.closest("[data-wl-modal]"));
          setTimeout(wlTryOpenCartDrawer, 250);
        } else {
          const err = await res.json().catch(() => ({}));
          if (statusEl) statusEl.textContent = err.description || "Something went wrong — please try again.";
          addBtn.disabled = false;
        }
      } catch (err) {
        console.error("WL Quiz: multi add to cart failed", err);
        if (statusEl) statusEl.textContent = "Something went wrong — please try again.";
        addBtn.disabled = false;
      }
    });
  }

  function initPeptideQuiz() {
    if (!qs('[data-wl-modal="peptide-quiz"]')) return;

    wlRenderQuizGoalCheckboxes();

    const submitBtn = qs("[data-wl-quiz-submit]");
    const retakeBtn = qs("[data-wl-quiz-retake]");
    const intakeEl = qs("[data-wl-quiz-intake]");
    const resultsEl = qs("[data-wl-quiz-results]");

    if (submitBtn) {
      submitBtn.addEventListener("click", () => {
        const statusEl = qs("[data-wl-quiz-status]");
        const consentEl = qs("[data-wl-quiz-consent]");

        if (!consentEl || !consentEl.checked) {
          if (statusEl) statusEl.textContent = "Please check the required box above to continue.";
          return;
        }

        const selectedGoals = qsa("[data-wl-quiz-goal]:checked").map((el) => el.value);
        if (selectedGoals.length === 0) {
          if (statusEl) statusEl.textContent = "Please select at least one goal above.";
          return;
        }

        const noInjectables = qs("[data-wl-quiz-no-injectables]")?.checked || false;
        const pregnancy = qs("[data-wl-quiz-pregnancy]")?.value || "no";
        const cancer = qs("[data-wl-quiz-cancer]")?.value || "no";

        const matches = wlScoreProducts(selectedGoals, noInjectables);
        wlRenderQuizResults(matches, { pregnancy, cancer });

        if (statusEl) statusEl.textContent = "";
        if (intakeEl) intakeEl.style.display = "none";
        if (resultsEl) resultsEl.style.display = "block";
      });
    }

    if (retakeBtn) {
      retakeBtn.addEventListener("click", () => {
        if (intakeEl) intakeEl.style.display = "block";
        if (resultsEl) resultsEl.style.display = "none";
      });
    }
  }

  // -----------------------------
  // Rivo Rewards — mirror live points/tier from the hidden Rivo Account
  // Dashboard block into our own card, and point the CTAs at the Rivo
  // "Ways to Redeem" block (visible, lower on this page) instead of a
  // dead #wl-addons anchor.
  // -----------------------------
  function wlParseRivoAccountCard(card) {
    const text = (card.textContent || "").replace(/\s+/g, " ").trim();
    const pointsMatch = text.match(/([\d,]+)\s*Points/i);
    const tierMatch = text.match(/on the\s+([A-Za-z][A-Za-z\s]*?)\s+tier/i);
    return {
      points: pointsMatch ? pointsMatch[1] : null,
      tier: tierMatch ? tierMatch[1].trim() : null,
    };
  }

  function wlSyncRivoRewards(attemptsLeft) {
    const balanceEl = qs("[data-wl-rewards-balance]");
    const tierEl = qs("[data-wl-rewards-tier]");
    const card = qs("#rivo-account-dashboard .rivo-account-card");

    if (card && (card.textContent || "").trim()) {
      const { points, tier } = wlParseRivoAccountCard(card);
      if (balanceEl && points) balanceEl.textContent = `${points} pts`;
      if (tierEl && tier) tierEl.textContent = `${tier} tier`;
      if (points) return; // got real data, stop polling
    }

    // Rivo's widget loads/fetches asynchronously — retry for a few seconds
    // before giving up and leaving the "—" placeholder.
    if (attemptsLeft > 0) {
      setTimeout(() => wlSyncRivoRewards(attemptsLeft - 1), 500);
    }
  }

  function wlScrollToRivoRewards() {
    const target =
      qs(".rivo-lp-section-title") ||
      qs(".rivo-lp-cards-container.rivo-rewards");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Fallback: Rivo block not found for some reason — don't strand the click.
      qs("#wl-addons")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function wlInsertShopAddonsButton() {
    if (qs("[data-wl-shop-addons-inline]")) return; // already inserted

    const rewardsBlock =
      qs(".rivo-lp-cards-container.rivo-rewards") ||
      qs(".rivo-lp-section-title");
    if (!rewardsBlock) return;

    // Walk up to a reasonably page-level container so the button sits
    // below the whole Rivo block rather than jammed inside its grid.
    const anchor = rewardsBlock.closest("[id^='shopify-block']") || rewardsBlock;

    const wrap = document.createElement("div");
    wrap.style.cssText = "max-width:1120px;margin:14px auto 0;padding:0 20px;text-align:center;";
    wrap.innerHTML = `
      <button type="button" data-wl-shop-addons-inline class="button button--primary">
        Shop add-ons →
      </button>
    `;
    anchor.insertAdjacentElement("afterend", wrap);

    qs("[data-wl-shop-addons-inline]", wrap).addEventListener("click", () => {
      qs("#wl-addons")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function initRivoRewards() {
    if (!qs("#rivo-account-dashboard")) return; // block not on this page

    wlSyncRivoRewards(14); // ~7s of retries

    const viewBtn = qs("[data-wl-view-rewards]");
    if (viewBtn) {
      viewBtn.addEventListener("click", (e) => {
        e.preventDefault();
        wlScrollToRivoRewards();
      });
    }

    // The Ways to Redeem block can also load async — retry insertion too.
    let insertAttempts = 14;
    (function tryInsert() {
      wlInsertShopAddonsButton();
      if (!qs("[data-wl-shop-addons-inline]") && insertAttempts > 0) {
        insertAttempts -= 1;
        setTimeout(tryInsert, 500);
      }
    })();
  }

  // -----------------------------
  // Telemed compliance popup
  //
  // Driven entirely by the daily compliance sweep
  // (rmh-credit-portal/scripts/booking-reconcile.mjs). That job is the ONLY
  // thing that decides who is out of compliance; this just surfaces its
  // verdict to the patient at login. The switch is the `needs-virtual`
  // customer tag - the same tag that enrols them in the GHL reminder ladder,
  // so the portal and the email/SMS ladder can never tell a patient two
  // different stories.
  //
  // The compliance card emits #wl-compliance-data. `state` is one of:
  //   hold    wl-grace-expired               -> shipments held now
  //   due     needs-virtual, still in grace  -> warn, nothing held yet
  //   current | exempt | none                -> no popup
  //
  // HOLD keys off wl-grace-expired, NOT needs-virtual (Derek, 2026-08-06):
  // needs-virtual is ladder ENROLMENT and gets stripped on exit/undo, so a
  // patient whose grace had genuinely expired could silently fall back to the
  // neutral card. wl-grace-expired is the durable fact.
  //
  // COPY RULE (hard, Derek 2026-08-04): never say the subscription is
  // paused/on hold/suspended. It isn't. Billing continues; only fulfilment
  // is withheld. Telling a patient their money stopped moving when it hasn't
  // is how you earn a chargeback.
  // -----------------------------
  const WL_COMPLIANCE_ACK_KEY = "wl_compliance_ack_v1";

  function wlReadComplianceData() {
    const el = document.getElementById("wl-compliance-data");
    if (!el) return null;
    let data;
    try {
      data = JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }

    // Preview override for staging: ?wl_compliance_preview=hold|due forces the
    // popup so both states can be reviewed without re-tagging a real patient.
    // Only overrides the MODAL - the card above stays server-rendered truth.
    try {
      const forced = new URLSearchParams(window.location.search).get("wl_compliance_preview");
      if (forced === "hold" || forced === "due") {
        data.state = forced;
        data.__preview = true;
      }
    } catch (err) {
      /* URLSearchParams unavailable - ignore */
    }
    return data;
  }

  // `hold` re-shows every session - a shipment-affecting notice should not be
  // permanently dismissible. `due` is a nudge, so once a day is enough.
  function wlComplianceAlreadyAcked(state) {
    try {
      if (state === "hold") {
        return sessionStorage.getItem(WL_COMPLIANCE_ACK_KEY) === "hold";
      }
      return localStorage.getItem(WL_COMPLIANCE_ACK_KEY) === new Date().toISOString().slice(0, 10);
    } catch (err) {
      return false; // storage blocked (private mode) -> show it
    }
  }

  function wlComplianceMarkAcked(state) {
    try {
      if (state === "hold") {
        sessionStorage.setItem(WL_COMPLIANCE_ACK_KEY, "hold");
      } else {
        localStorage.setItem(WL_COMPLIANCE_ACK_KEY, new Date().toISOString().slice(0, 10));
      }
    } catch (err) {
      /* storage blocked - popup just shows again, which is the safe failure */
    }
  }

  function wlComplianceCopy(d) {
    const seen = d.lastVisit
      ? "Our records show your last visit was <strong>" + d.lastVisit + "</strong>."
      : "We don’t have a telemedicine visit on record for you yet.";

    const cadence =
      "Tennessee patients need to be seen every 180 days, and out-of-state patients every 90 days.";

    if (d.state === "hold") {
      return {
        pill: "Action needed",
        pillColor: "#b3261e",
        // One title for both visit types. `wl-needs-initial` means "no virtual
        // visit on record", NOT "never received a shipment" — a patient seen
        // in-office for years carries it too, so "first shipment" was false
        // for them. "Next" is true either way. (Derek, 2026-08-06)
        title: "We need to see you before your next shipment goes out",
        body:
          '<p style="margin:0 0 10px;">' + seen + " " + cadence +
          (d.dueDate
            ? " Yours came due on <strong>" + d.dueDate + "</strong>, and the grace period has now passed."
            : "") +
          '</p><p style="margin:0;">Because of that, we have to hold your shipments until you’ve been seen. ' +
          "Book a visit and we’ll release them right away — most appointments are available within a few days.</p>",
        billing:
          "<strong>Your plan stays active and your billing continues as normal.</strong> " +
          "It’s the shipment we have to hold, not your plan — so nothing about your pricing " +
          "or your place in the program changes."
      };
    }

    // due - still inside the grace window, nothing held yet
    return {
      pill: "Visit due",
      pillColor: "#ad8952",
      title: "It’s time for your next visit",
      body:
        '<p style="margin:0 0 10px;">' + seen + " " + cadence + '</p><p style="margin:0;">' +
        (d.holdDate
          ? "Please book by <strong>" + d.holdDate + "</strong>. If we get past that date without seeing you, " +
            "we have to start holding your shipments until the visit is done."
          : "Please get a visit booked so there’s no interruption to your shipments.") +
        "</p>",
      billing:
        "If shipments do get held, <strong>your plan stays active and billing continues as normal</strong> — " +
        "it’s the shipment we hold, not your plan. Booking now avoids the whole thing."
    };
  }

  function initTelemedCompliance() {
    const data = wlReadComplianceData();
    if (!data) return;
    if (data.state !== "hold" && data.state !== "due") return;
    if (!data.__preview && wlComplianceAlreadyAcked(data.state)) return;

    const modal = qs('[data-wl-modal="telemed-compliance"]');
    if (!modal) return;

    const copy = wlComplianceCopy(data);

    const pill = qs("[data-wl-compliance-pill]", modal);
    if (pill) {
      pill.textContent = copy.pill;
      pill.style.background = copy.pillColor;
    }

    const title = qs("[data-wl-compliance-title]", modal);
    if (title) title.textContent = copy.title;

    const body = qs("[data-wl-compliance-body]", modal);
    if (body) body.innerHTML = copy.body;

    const billing = qs("[data-wl-compliance-billing]", modal);
    if (billing) {
      billing.innerHTML = copy.billing;
      billing.style.borderLeftColor = copy.pillColor;
    }

    const book = qs("[data-wl-compliance-book]", modal);
    if (book) {
      book.href = data.bookUrl;
      book.textContent = data.bookLabel;
    }

    // Mark acknowledged on any exit, including booking - if they click through
    // to book, don't nag them again on the way back.
    qsa("[data-wl-modal-close]", modal).forEach(function (btn) {
      btn.addEventListener("click", function () { wlComplianceMarkAcked(data.state); });
    });
    if (book) book.addEventListener("click", function () { wlComplianceMarkAcked(data.state); });

    // Small delay so the page (and Rivo's async blocks) settle first. Skipped
    // if the patient already opened something else.
    setTimeout(function () {
      if (qsa('[data-wl-modal][aria-hidden="false"]').length) return;
      openModalByName("telemed-compliance");
    }, 900);
  }

  // -----------------------------
  // Weekly insight (motivation / expectation-setting)
  //
  // Hand-curated explainers on why weight loss takes time — the featured
  // one rotates automatically each week (epoch-week % length, no backend),
  // and the arrows let members browse the whole library. To refresh the
  // content periodically, edit this array and push the asset. Keep the
  // pace claims consistent with the rest of the portal (1-2 lb/week).
  // -----------------------------
  const WL_MOTIVATION = [
    {
      title: "Why the first ten pounds came off so fast",
      text: "A big share of early weight loss is water, not fat. As appetite settles and insulin levels drop, your body releases retained sodium and water, inflammation cools down, and stored carbohydrate (which holds about three grams of water per gram) gets used up. Real fat loss is happening underneath — but that dramatic first drop is partly your body de-puffing.",
      takeaway: "So when the pace slows after the first few weeks, nothing broke. That's the handoff from water loss to true fat loss — and the slower pounds are the ones that change your body."
    },
    {
      title: "The law of diminishing returns — and why it's a good sign",
      text: "A smaller body simply burns fewer calories than a larger one, at rest and in motion. The exact same effort that produced two pounds a week at your starting weight might produce one pound a week twenty pounds later. The medication didn't stop working — the math changed, because you changed it.",
      takeaway: "Slower loss at a lower weight isn't a stall. It's arithmetic proof of how far you've come."
    },
    {
      title: "Plateaus: your body catching its breath",
      text: "Weight loss is a staircase, not a slide. Your body periodically pauses to recalibrate — metabolism adapts, water shifts can hide a week or two of genuine fat loss, and then the scale often drops suddenly in a 'whoosh.' Two flat weeks on the scale very rarely means two weeks without progress.",
      takeaway: "Judge your progress by the monthly trend, never by any single week. If a plateau stretches past three or four weeks, that's exactly what your check-ins and our team are for."
    },
    {
      title: "Why some people lose faster than others",
      text: "Starting weight, muscle mass, age, sex, sleep, stress, genetics, and how your body responds to the medication all set your personal pace. Two people on identical doses, eating identically, will lose at different rates — every time. Comparing your week to someone else's is comparing two different equations.",
      takeaway: "The only fair comparison is you versus your own starting point. That trend line in your BMI tracker is the whole story."
    },
    {
      title: "The scale lies — day to day",
      text: "Your weight can swing two to five pounds in a single day from salt, carbohydrates, hydration, hormones, and digestion — none of which is fat. A salty dinner can 'gain' three pounds overnight; none of it is real. Daily weigh-ins measure noise, not progress.",
      takeaway: "Weigh in once a week — same day, same time, same conditions — and log it here in the portal. The trend is true even when any single number isn't."
    },
    {
      title: "Protect your muscle — it's your engine",
      text: "Without enough protein and some resistance, part of any weight loss comes from muscle — and muscle is what keeps your metabolism running high. Prioritizing protein and staying active steers the loss toward fat, which can make the scale move a touch slower while your body composition improves much faster.",
      takeaway: "A slightly slower loss that's nearly all fat beats a fast loss that costs you muscle. Protein first, every meal — your macro targets below make it easy."
    },
    {
      title: "Victories the scale can't see",
      text: "Clothes fitting differently. Food noise going quiet. Better sleep, better labs, less joint pain, more energy on the stairs. These changes usually show up before the scale fully catches up — and they're better predictors of long-term success than any single weigh-in.",
      takeaway: "Notice one non-scale victory this week and hold onto it. That's the program working, whatever the scale says today."
    },
    {
      title: "Your dose is a journey, not a setting",
      text: "GLP-1 medications are started low and stepped up gradually on purpose — it keeps side effects manageable while your body adjusts. Many people don't reach their most effective dose for a few months, which means your early results are a preview, not the finale.",
      takeaway: "If appetite control feels incomplete right now, that's often a dose-finding phase, not a verdict. Mention it in your monthly check-in and we'll look at it together."
    },
    {
      title: "One to two pounds a week is winning",
      text: "It doesn't sound dramatic — but a pound and a half a week is over seventy-five pounds in a year. That pace protects your muscle, gives your skin time to adapt, and builds habits that keep the weight off. Crash pace almost always rebounds; steady pace almost never does.",
      takeaway: "You're not trying to lose weight fast. You're trying to lose it once."
    },
    {
      title: "Consistency beats perfection",
      text: "One rough weekend cannot undo real progress — gaining a single pound of actual fat requires roughly 3,500 calories beyond what your body burns, and a temporary jump on the scale after a big meal is almost entirely water. What decides your outcome is what you do most of the time, not what you did once.",
      takeaway: "Never let one off-plan day become an off-plan week. Next meal, next dose, back on track — that's the entire skill."
    }
  ];

  function initMotivation() {
    const titleEl = qs("[data-wl-insight-title]");
    const textEl = qs("[data-wl-insight-text]");
    const takeawayEl = qs("[data-wl-insight-takeaway]");
    const countEl = qs("[data-wl-insight-count]");
    const bodyEl = qs("[data-wl-insight-body]");
    if (!titleEl || !textEl) return;

    const n = WL_MOTIVATION.length;
    let idx = Math.floor(Date.now() / 604800000) % n; // rotates every 7 days

    function render() {
      const item = WL_MOTIVATION[idx];
      titleEl.textContent = item.title;
      textEl.textContent = item.text;
      takeawayEl.textContent = item.takeaway;
      if (countEl) countEl.textContent = (idx + 1) + " of " + n;
    }

    function step(delta) {
      idx = (idx + delta + n) % n;
      if (!bodyEl) { render(); return; }
      bodyEl.classList.add("is-fading");
      setTimeout(function () {
        render();
        bodyEl.classList.remove("is-fading");
      }, 150);
    }

    const prev = qs("[data-wl-insight-prev]");
    const next = qs("[data-wl-insight-next]");
    if (prev) prev.addEventListener("click", function () { step(-1); });
    if (next) next.addEventListener("click", function () { step(1); });

    render();
  }

  // -----------------------------
  // Monthly check-in (satisfaction survey -> retention alert)
  //
  // Every answer is worth 0-3 points; 8 questions, 24 max. Every completed
  // check-in fires a Klaviyo event (event + profile properties, same
  // dual-write rule as the landing-page quiz — flows read events, segments
  // read profiles). A total at or below WL_CHECKIN_ALERT_MAX, or ANY answer
  // marked flag:true, additionally POSTs the full payload to the GHL
  // inbound-webhook URL from the section setting so staff get an internal
  // notification email and can reach out before the member drops off.
  // Positive check-ins never hit the webhook.
  // -----------------------------
  const WL_CHECKIN_KEY = "wl_checkin_last_v1";
  const WL_CHECKIN_HEIGHT_KEY = "wl_checkin_height_v1"; // {ft,in} — asked once, prefilled after
  const WL_CHECKIN_DUE_DAYS = 7;
  const WL_CHECKIN_ALERT_MAX = 15; // all-"mostly good" answers = 16; anything below alerts
  const WL_CHECKIN_KLAVIYO_ID = "Sqyivs";       // public company_id only — same as soph-wl-landing
  const WL_CHECKIN_KLAVIYO_REV = "2024-02-15";
  const WL_CHECKIN_METRIC = "WL Weekly Check-in Completed";

  const WL_CHECKIN_QUESTIONS = [
    { k: "overall", t: "Overall, how are you feeling about your program?",
      opts: [
        { label: "Great — really happy with it", pts: 3 },
        { label: "Good", pts: 2 },
        { label: "Just okay", pts: 1 },
        { label: "Not good", pts: 0, flag: "unhappy with program" }
      ] },
    { k: "progress", t: "How is your weight trending?",
      opts: [
        { label: "Losing steadily", pts: 3 },
        { label: "Losing slowly but surely", pts: 2 },
        { label: "Stuck at a plateau", pts: 1 },
        { label: "Trending up", pts: 0 }
      ] },
    { k: "medication", t: "How well is your medication controlling hunger and cravings?",
      opts: [
        { label: "Very well", pts: 3 },
        { label: "Pretty well", pts: 2 },
        { label: "Only a little", pts: 1 },
        { label: "Not at all", pts: 0 }
      ] },
    { k: "side_effects", t: "How are side effects treating you?",
      opts: [
        { label: "None to speak of", pts: 3 },
        { label: "Mild and manageable", pts: 2 },
        { label: "Bothersome", pts: 1 },
        { label: "Rough — they affect my daily life", pts: 0, flag: "significant side effects" }
      ] },
    { k: "energy", t: "Energy and overall well-being compared to before you started?",
      opts: [
        { label: "Much better", pts: 3 },
        { label: "Somewhat better", pts: 2 },
        { label: "About the same", pts: 1 },
        { label: "Worse", pts: 0 }
      ] },
    { k: "adherence", t: "Are you able to take your dose on schedule?",
      opts: [
        { label: "Every week, like clockwork", pts: 3 },
        { label: "Most weeks", pts: 2 },
        { label: "I've missed a couple lately", pts: 1 },
        { label: "I'm struggling to stay on track", pts: 0 }
      ] },
    { k: "support", t: "Do you feel supported by our team?",
      opts: [
        { label: "Absolutely", pts: 3 },
        { label: "Mostly", pts: 2 },
        { label: "Could be better", pts: 1 },
        { label: "Not really", pts: 0, flag: "feels unsupported" }
      ] },
    { k: "continue", t: "How likely are you to continue with the program next month?",
      opts: [
        { label: "Definitely continuing", pts: 3 },
        { label: "Probably", pts: 2 },
        { label: "Not sure yet", pts: 1, flag: "unsure about continuing" },
        { label: "I'm thinking about stopping", pts: 0, flag: "considering stopping" }
      ] }
  ];

  function wlCheckinConfig() {
    try {
      const el = document.getElementById("wl-checkin-config");
      return el ? JSON.parse(el.textContent) : null;
    } catch (_) { return null; }
  }

  function wlCheckinLastDone() {
    try {
      const raw = localStorage.getItem(WL_CHECKIN_KEY);
      if (!raw) return null;
      const d = new Date(raw);
      return isNaN(d) ? null : d;
    } catch (_) { return null; }
  }

  function wlCheckinRenderCardState() {
    const badge = qs("[data-wl-checkin-due]");
    const lastLine = qs("[data-wl-checkin-lastdone]");
    const last = wlCheckinLastDone();
    const due = !last || ((Date.now() - last.getTime()) / 86400000) >= WL_CHECKIN_DUE_DAYS;
    if (badge) badge.style.display = due ? "inline-block" : "none";
    if (lastLine && last) {
      lastLine.textContent = "Last check-in: " + last.toLocaleDateString(undefined, { month: "long", day: "numeric" }) + (due ? " — time for another." : ". You're all set for this week.");
      lastLine.style.display = "block";
    }
  }

  function wlCheckinKlaviyo(email, props) {
    // Dual write: event properties for flows, profile properties for
    // segments/campaigns. Never allowed to block or fail the check-in.
    return fetch("https://a.klaviyo.com/client/events/?company_id=" + WL_CHECKIN_KLAVIYO_ID, {
      method: "POST",
      headers: { "Content-Type": "application/json", "revision": WL_CHECKIN_KLAVIYO_REV },
      body: JSON.stringify({ data: { type: "event", attributes: {
        properties: props,
        metric: { data: { type: "metric", attributes: { name: WL_CHECKIN_METRIC } } },
        profile: { data: { type: "profile", attributes: { email: email, properties: {
          "Last WL Check-in": props["Completed At"],
          "Last WL Check-in Score": props["Score"],
          "Last WL Check-in Percent": props["Score Percent"],
          "Last WL Check-in Flagged": props["Flagged"]
        } } } }
      } } })
    }).catch(function () {});
  }

  function wlCheckinAlert(webhook, payload) {
    if (!webhook || webhook.indexOf("http") !== 0 || webhook.indexOf("example.com") !== -1) {
      console.warn("[WL] Check-in alert webhook not configured — low score NOT forwarded.", payload);
      return Promise.resolve();
    }
    return fetch(webhook, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    }).catch(function (err) { console.error("[WL] Check-in alert webhook failed:", err); });
  }

  function initCheckin() {
    const modal = qs('[data-wl-modal="checkin"]');
    if (!modal) return;

    wlCheckinRenderCardState();

    const cfg = wlCheckinConfig() || {};
    const customer = cfg.customer || {};

    const surveyEl = qs("[data-wl-checkin-survey]", modal);
    const thanksEl = qs("[data-wl-checkin-thanks]", modal);
    const stepEl = qs("[data-wl-checkin-step]", modal);
    const titleEl = qs("[data-wl-checkin-title]", modal);
    const optsEl = qs("[data-wl-checkin-options]", modal);
    const fillEl = qs("[data-wl-checkin-fill]", modal);
    const backBtn = qs("[data-wl-checkin-back]", modal);
    const numbersWrap = qs("[data-wl-checkin-numbers]", modal);
    const weightEl = qs("[data-wl-checkin-weight]", modal);
    const feetEl = qs("[data-wl-checkin-feet]", modal);
    const inchesEl = qs("[data-wl-checkin-inches]", modal);
    const doseEl = qs("[data-wl-checkin-dose]", modal);
    const waistEl = qs("[data-wl-checkin-waist]", modal);
    const hipsEl = qs("[data-wl-checkin-hips]", modal);
    const armsEl = qs("[data-wl-checkin-arms]", modal);
    const legsEl = qs("[data-wl-checkin-legs]", modal);
    const goalWrap = qs("[data-wl-checkin-goal-wrap]", modal);
    const goalEl = qs("[data-wl-checkin-goal]", modal);
    const goalLockEl = qs("[data-wl-checkin-goal-lock]", modal);
    const numbersErr = qs("[data-wl-checkin-numbers-err]", modal);
    const numbersNext = qs("[data-wl-checkin-numbers-next]", modal);
    const commentWrap = qs("[data-wl-checkin-comment-wrap]", modal);
    const commentEl = qs("[data-wl-checkin-comment]", modal);
    const submitBtn = qs("[data-wl-checkin-submit]", modal);
    const statusEl = qs("[data-wl-checkin-status]", modal);

    // Step 0 = weigh-in + dose ("your numbers"), then the questions, then
    // the free-text comment. numbers = {weightLb, bmi, doseMg} once step 0
    // validates.
    const TOTAL_STEPS = WL_CHECKIN_QUESTIONS.length + 2;
    let step = 0;
    let answers = {};
    let numbers = null;
    let submitting = false;
    let doseWarnedFor = null; // high-dose warning: remembers the value already warned about
    let pendingGoalLb = null; // goal typed + lock-checked this session, saved on submit
    let wlMacros = null;      // macro targets on file, rides along on the check-in payload

    // Whether a member has macro targets — and how stale they are — is a
    // headline signal for the flagged-check-in outreach, so it travels with
    // the check-in rather than having to be looked up separately later.
    function refreshMacroTargets() {
      fetch("/apps/wl-hub/macros")
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.ok) {
            wlMacros = data.macros || null;
            // One source of truth for "do they have targets" — the thank-you
            // screen's nudge reads this too.
            wlMacrosOnFile = wlMacros;
          }
        })
        .catch(function () {});
    }

    // Ask the server whether a goal is on file; the set-once block only shows
    // when there isn't. Non-blocking — if it hasn't answered by the numbers
    // step, the block just stays hidden and we ask again next week.
    function refreshGoalPrompt() {
      fetch("/apps/wl-hub/goal")
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (!data.ok) return;
          wlGoalWeight = data.goal || null;
          wlGoalKnown = true;
          if (goalWrap) goalWrap.style.display = wlGoalWeight ? "none" : "block";
          const lockedLine = qs("[data-wl-checkin-goal-locked]", modal);
          const lockedVal = qs("[data-wl-checkin-goal-locked-value]", modal);
          if (lockedLine) lockedLine.style.display = wlGoalWeight ? "block" : "none";
          if (lockedVal && wlGoalWeight) lockedVal.textContent = wlGoalWeight.goalLb + " lb";
        })
        .catch(function () {});
    }

    function prefillHeight() {
      try {
        const h = JSON.parse(localStorage.getItem(WL_CHECKIN_HEIGHT_KEY) || "null");
        if (h && feetEl && !feetEl.value) feetEl.value = h.ft;
        if (h && inchesEl && !inchesEl.value) inchesEl.value = h.in;
      } catch (_) {}
    }

    function render() {
      surveyEl.style.display = "block";
      thanksEl.style.display = "none";
      if (fillEl) fillEl.style.width = Math.round((step / TOTAL_STEPS) * 100) + "%";
      if (backBtn) backBtn.style.visibility = step > 0 ? "visible" : "hidden";
      if (statusEl) statusEl.textContent = "";

      const onNumbers = step === 0;
      const onComment = step >= TOTAL_STEPS - 1;
      if (numbersWrap) numbersWrap.style.display = onNumbers ? "block" : "none";
      commentWrap.style.display = onComment ? "block" : "none";
      optsEl.style.display = (onNumbers || onComment) ? "none" : "flex";

      if (onNumbers) {
        if (stepEl) stepEl.textContent = "First, your numbers";
        if (titleEl) titleEl.textContent = "This week's weigh-in and dose";
        if (numbersErr) numbersErr.textContent = "";
        prefillHeight();
        return;
      }

      if (onComment) {
        if (stepEl) stepEl.textContent = "Last one";
        if (titleEl) titleEl.textContent = "Anything you'd like us to know?";
        return;
      }

      const qIdx = step - 1;
      const q = WL_CHECKIN_QUESTIONS[qIdx];
      if (stepEl) stepEl.textContent = "Question " + (qIdx + 1) + " of " + WL_CHECKIN_QUESTIONS.length;
      if (titleEl) titleEl.textContent = q.t;
      optsEl.innerHTML = "";
      q.opts.forEach(function (opt, idx) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "wl-checkin-pill" + (answers[q.k] === idx ? " is-selected" : "");
        btn.textContent = opt.label;
        btn.addEventListener("click", function () {
          answers[q.k] = idx;
          step += 1;
          render();
        });
        optsEl.appendChild(btn);
      });
    }

    if (numbersNext) numbersNext.addEventListener("click", function () {
      const weightLb = weightEl ? parseFloat(weightEl.value) : NaN;
      const ft = feetEl ? parseFloat(feetEl.value) : NaN;
      const inch = inchesEl ? parseFloat(inchesEl.value) : 0;
      const doseRaw = doseEl ? doseEl.value.trim() : "";
      const doseMg = doseRaw === "" ? null : parseFloat(doseRaw);

      if (!isFinite(weightLb) || weightLb < 60 || weightLb > 800) {
        if (numbersErr) numbersErr.textContent = "Please enter a valid weight in pounds.";
        return;
      }
      const heightIn = (isFinite(ft) ? ft : 0) * 12 + (isFinite(inch) ? inch : 0);
      if (!isFinite(heightIn) || heightIn < 36 || heightIn > 96) {
        if (numbersErr) numbersErr.textContent = "Please enter a valid height (feet and inches).";
        return;
      }
      if (doseRaw !== "" && (!isFinite(doseMg) || doseMg <= 0)) {
        if (numbersErr) numbersErr.textContent = "Please enter your dose in mg — or use the converter if you only know units.";
        return;
      }
      // A dose typed in units by mistake is the classic error this flow
      // replaced the confirm-modal for. Derek 2026-08-11: anything over
      // 15 mg (the top weekly tirzepatide dose) prompts a warning — a
      // second Continue click accepts the value, so a genuine outlier
      // isn't hard-blocked. Changing the number re-arms the warning.
      if (doseMg !== null && doseMg > 15 && doseWarnedFor !== doseMg) {
        doseWarnedFor = doseMg;
        if (numbersErr) numbersErr.textContent = "That's higher than any usual weekly dose — doses over 15 mg are almost always units, not mg. Double-check it (the converter link below can help), or press Continue again if " + doseMg + " mg is really correct.";
        return;
      }

      // Optional tape measurements (inches) - independent, validated only if present.
      const measurements = {};
      const tapeFields = [[waistEl, "waistIn", "waist"], [hipsEl, "hipsIn", "hips"], [armsEl, "armsIn", "arms"], [legsEl, "legsIn", "legs"]];
      for (const pair of tapeFields) {
        const el = pair[0], key = pair[1], label = pair[2];
        if (!el || el.value.trim() === "") continue;
        const v = parseFloat(el.value);
        if (!isFinite(v) || v < 5 || v > 120) {
          if (numbersErr) numbersErr.textContent = "That " + label + " measurement doesn't look right — inches, please (or leave it blank).";
          return;
        }
        measurements[key] = Math.round(v * 10) / 10;
      }

      // Goal weight (optional, set-once): a typed goal needs the lock-in box.
      pendingGoalLb = null;
      if (goalWrap && goalWrap.style.display !== "none" && goalEl && goalEl.value.trim() !== "") {
        const goalLb = parseFloat(goalEl.value);
        if (!isFinite(goalLb) || goalLb < 60 || goalLb > 800) {
          if (numbersErr) numbersErr.textContent = "Please enter a realistic goal weight in pounds (or leave it blank for now).";
          return;
        }
        if (goalLb >= weightLb) {
          if (numbersErr) numbersErr.textContent = "Your goal should be below your current weight — double-check the number.";
          return;
        }
        if (!goalLockEl || !goalLockEl.checked) {
          if (numbersErr) numbersErr.textContent = "Check the “lock it in” box to confirm your goal — or clear it to decide later.";
          return;
        }
        pendingGoalLb = Math.round(goalLb * 10) / 10;
      }

      const bmi = Math.round(bmiFromImperial(weightLb, heightIn) * 10) / 10;
      numbers = { weightLb: Math.round(weightLb * 10) / 10, heightIn: heightIn, bmi: bmi, doseMg: doseMg,
        measurements: Object.keys(measurements).length ? measurements : null };
      try { localStorage.setItem(WL_CHECKIN_HEIGHT_KEY, JSON.stringify({ ft: ft, in: isFinite(inch) ? inch : 0 })); } catch (_) {}
      step = 1;
      render();
    });

    function reset() {
      step = 0;
      answers = {};
      numbers = null;
      submitting = false;
      doseWarnedFor = null;
      pendingGoalLb = null;
      if (weightEl) weightEl.value = "";
      if (doseEl) doseEl.value = "";
      if (commentEl) commentEl.value = "";
      if (goalEl) goalEl.value = "";
      if (goalLockEl) goalLockEl.checked = false;
      [waistEl, hipsEl, armsEl, legsEl].forEach(function (el) { if (el) el.value = ""; });
      if (goalWrap) goalWrap.style.display = "none";
      refreshGoalPrompt();
      refreshMacroTargets();
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = "Finish my check-in"; }
      render();
    }

    if (backBtn) backBtn.addEventListener("click", function () {
      if (step > 0) { step -= 1; render(); }
    });

    qsa('[data-wl-open-modal="checkin"]').forEach(function (btn) {
      btn.addEventListener("click", reset);
    });

    // Anything the server refused during the last submit. Non-empty = tell the
    // member their numbers did not save, instead of thanking them for nothing.
    let saveFailures = [];

    function showThanks(flagged) {
      surveyEl.style.display = "none";
      thanksEl.style.display = "block";
      const title = qs("[data-wl-checkin-thanks-title]", modal);
      const body = qs("[data-wl-checkin-thanks-body]", modal);
      const cta = qs("[data-wl-checkin-thanks-cta]", modal);
      if (flagged) {
        if (title) title.textContent = "Thank you — we're on it.";
        if (body) body.innerHTML = "It sounds like things could be going better, and we take that seriously. <strong>A member of our team will personally reach out soon.</strong> If you'd rather not wait, book a follow-up visit right now, or call or chat with us — we'd love to talk it through.";
        if (cta) cta.style.display = "flex";
      } else {
        if (title) title.textContent = "Thank you! Keep it up.";
        if (body) body.innerHTML = "We love seeing your progress. Your answers help us keep your plan dialed in — see you next week. In the meantime, the chat button is always here if anything comes up.";
        if (cta) cta.style.display = "none";
      }
      const bmiValueEl = qs("[data-wl-checkin-bmi-value]", modal);
      if (bmiValueEl && numbers) bmiValueEl.textContent = numbers.bmi.toFixed(1);
      // Nudge anyone with no targets on file toward the calculator — the
      // natural "what next" beat, and it costs nothing when they already have
      // them because wlRefreshMacroPrompt hides it.
      wlRefreshMacroPrompt();
      /* A refused write gets said out loud. The survey answers still reached
         Klaviyo, so this is not a total failure - but the member must not walk
         away believing a weight or dose is on file when it is not. Their
         provider reads those numbers. */
      if (body && saveFailures.length) {
        const what = saveFailures.join(" and ");
        body.innerHTML += "<span style=\"display:block;margin-top:12px;padding:10px 14px;" +
          "background:#fdf2f2;border-left:3px solid #b3261e;color:#8a1c16;font-size:15px;\">" +
          "<strong>One thing didn't save.</strong> We couldn't record your " + what +
          " just now. Your answers came through, but please try the check-in again later " +
          "or call the office so we can add it to your chart.</span>";
      }
    }

    if (submitBtn) submitBtn.addEventListener("click", function () {
      if (submitting) return;
      submitting = true;
      saveFailures = [];
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending…";

      let score = 0;
      const flags = [];
      const answerProps = {};
      WL_CHECKIN_QUESTIONS.forEach(function (q) {
        const opt = q.opts[answers[q.k]];
        if (!opt) return;
        score += opt.pts;
        if (opt.flag) flags.push(opt.flag);
        answerProps["Check-in: " + q.k] = opt.label;
      });
      const maxScore = WL_CHECKIN_QUESTIONS.length * 3;
      const pct = Math.round((score / maxScore) * 100);
      const comment = commentEl ? commentEl.value.trim() : "";
      const flagged = score <= WL_CHECKIN_ALERT_MAX || flags.length > 0;
      const now = new Date().toISOString();

      const props = Object.assign({
        "Score": score,
        "Max Score": maxScore,
        "Score Percent": pct,
        "Flagged": flagged ? "Yes" : "No",
        "Flag Reasons": flags.join("; "),
        "Comment": comment,
        "Completed At": now,
        "Weight (lb)": numbers ? numbers.weightLb : null,
        "BMI": numbers ? numbers.bmi : null,
        "Dose (mg)": numbers && numbers.doseMg !== null ? numbers.doseMg : "",
        "Waist (in)": (numbers && numbers.measurements && numbers.measurements.waistIn) || "",
        "Hips (in)": (numbers && numbers.measurements && numbers.measurements.hipsIn) || "",
        "Arms (in)": (numbers && numbers.measurements && numbers.measurements.armsIn) || "",
        "Legs (in)": (numbers && numbers.measurements && numbers.measurements.legsIn) || "",
        // Macro targets on file. "Has Macro Targets" is deliberately a plain
        // Yes/No string so it can gate a Klaviyo flow directly — the absence
        // of targets is as useful an outreach hook as their contents.
        "Has Macro Targets": wlMacros ? "Yes" : "No",
        "Calorie Target": wlMacros ? wlMacros.calories : "",
        "Protein Target (g)": wlMacros ? wlMacros.proteinG : "",
        "Carb Target (g)": wlMacros ? wlMacros.carbsG : "",
        "Fat Target (g)": wlMacros ? wlMacros.fatG : "",
        "Macro Plan": wlMacros ? wlMacros.template : "",
        "Macros Updated": wlMacros ? String(wlMacros.updatedAt).slice(0, 10) : ""
      }, answerProps);

      // Mirror the weigh-in into the local BMI log (overwrite same-day entry,
      // same rule the old standalone tracker used) so the graph has an
      // offline fallback.
      const entryDate = todayISO();
      if (numbers) {
        const entries = storageLoadBmiLog();
        const entry = { date: entryDate, weight_lb: numbers.weightLb, bmi: numbers.bmi };
        const existingIdx = entries.findIndex(function (e) { return e.date === entryDate; });
        if (existingIdx >= 0) entries[existingIdx] = entry; else entries.push(entry);
        storageSaveBmiLog(entries);
      }

      const done = [];

      // Server persistence — same endpoints the standalone BMI tracker and
      // hero dose card used (customer note + metafield via the app proxy).
      let serverLog = null;
      if (numbers) {
        done.push(
          fetch("/apps/wl-hub/bmi", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ date: entryDate, weightLb: numbers.weightLb, bmi: numbers.bmi, measurements: numbers.measurements })
          }).then(function (res) { return res.json().catch(function () { return {}; }).then(function (data) {
            if (res.ok && data.ok && data.log) serverLog = data.log;
            else saveFailures.push("weigh-in");
            if (data && data.goal !== undefined && data.goal) { wlGoalWeight = data.goal; wlGoalKnown = true; }
            /* The reward card updates the moment a check-in lands - watching
               the bar tick up is the whole point of the mechanic. */
            if (res.ok && data.ok) wlRenderReward(data.log || [], data.reward);
          }); }).catch(function (err) { saveFailures.push("weigh-in"); console.error("[WL] Check-in BMI save failed:", err); })
        );
        if (numbers.doseMg !== null) {
          // Check res.ok AND data.ok, not just the network promise. A bare
          // .catch() only fires on a transport error, so an HTTP 401/500 read
          // as success: between 2026-08-11 and 08-18 the app proxy 401'd every
          // request and a week of doses went unwritten while the modal said
          // "Thanks". A write we cannot confirm must not report success.
          done.push(
            fetch("/apps/wl-hub/dose", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ dose: numbers.doseMg })
            }).then(function (res) {
              return res.json().catch(function () { return {}; }).then(function (data) {
                if (!res.ok || !data.ok) {
                  saveFailures.push("dose");
                  console.error("[WL] Check-in dose save rejected:", res.status, data);
                }
              });
            }).catch(function (err) {
              saveFailures.push("dose");
              console.error("[WL] Check-in dose save failed:", err);
            })
          );
        }
      }

      // Locked goal weight — set-once; the server refuses any second write.
      if (pendingGoalLb !== null) {
        done.push(
          fetch("/apps/wl-hub/goal", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ goalLb: pendingGoalLb })
          }).then(function (res) { return res.json().catch(function () { return {}; }).then(function (data) {
            if (data.goal) { wlGoalWeight = data.goal; wlGoalKnown = true; }
          }); }).catch(function (err) { console.error("[WL] Goal save failed:", err); })
        );
      }

      if (customer.email) done.push(wlCheckinKlaviyo(customer.email, props));
      if (flagged) {
        // Pre-formatted alert body (Derek 2026-08-12): GHL's notification
        // editor kept dropping the labels/line breaks, so the readable
        // message is built HERE and dropped into the email as ONE merge
        // field. `summary` uses <br> (GHL renders the body as HTML);
        // `summary_text` is the plain-newline twin as a fallback.
        const who = ((customer.first_name || "") + " " + (customer.last_name || "")).trim() || "A member";
        const lines = [
          "FLAGGED: " + who + "  —  " + score + "/" + maxScore + " (" + pct + "%)",
          "WHY: " + (flags.join("; ") || "low overall score"),
          "THEY SAID: " + (comment || "(no comment left)"),
          "NUMBERS: " + (numbers ? numbers.weightLb + " lb · BMI " + numbers.bmi : "not logged") +
            (numbers && numbers.doseMg !== null ? " · dose " + numbers.doseMg + " mg" : ""),
          "MACROS: " + (wlMacros
            ? wlMacros.calories + " kcal · P " + wlMacros.proteinG + "g / C " + wlMacros.carbsG +
              "g / F " + wlMacros.fatG + "g (set " + String(wlMacros.updatedAt).slice(0, 10) + ")"
            : "NEVER RUN THE CALCULATOR"),
          "PHONE: " + (customer.phone || "(none on file)"),
          "EMAIL: " + (customer.email || "(none on file)")
        ];
        const summaryText = lines.join(String.fromCharCode(10));
        const summaryHtml = lines.join("<br>");

        done.push(wlCheckinAlert(cfg.webhook, {
          type: "wl_checkin_alert",
          summary: summaryHtml,
          summary_text: summaryText,
          email: customer.email || "",
          first_name: customer.first_name || "",
          last_name: customer.last_name || "",
          phone: customer.phone || "",
          shopify_customer_id: customer.id || "",
          score: score,
          max_score: maxScore,
          score_percent: pct,
          flag_reasons: flags.join("; ") || "low overall score",
          comment: comment,
          weight_lb: numbers ? numbers.weightLb : "",
          bmi: numbers ? numbers.bmi : "",
          dose_mg: numbers && numbers.doseMg !== null ? numbers.doseMg : "",
          has_macro_targets: wlMacros ? "Yes" : "No",
          calorie_target: wlMacros ? wlMacros.calories : "",
          protein_target_g: wlMacros ? wlMacros.proteinG : "",
          carb_target_g: wlMacros ? wlMacros.carbsG : "",
          fat_target_g: wlMacros ? wlMacros.fatG : "",
          macro_plan: wlMacros ? wlMacros.template : "",
          macros_updated: wlMacros ? String(wlMacros.updatedAt).slice(0, 10) : "",
          answers: answerProps,
          completed_at: now,
          portal_url: location.href.split("?")[0]
        }));
      }

      try { localStorage.setItem(WL_CHECKIN_KEY, now); } catch (_) {}

      function finish() {
        if (!submitting) return;
        submitting = false;
        wlCheckinRenderCardState();
        showThanks(flagged);
        // Graph: prefer the fresh server log from the POST; otherwise fetch
        // it (wlLoadBmiGraph falls back to its own error copy), and if the
        // server is unreachable render the local log so the member still
        // sees their trend.
        if (serverLog) {
          const statusEl2 = qs("[data-wl-bmi-graph-status]");
          if (statusEl2) statusEl2.textContent = "";
          wlRenderBmiChart(serverLog);
          wlCheckBmiRate(serverLog);
          wlRenderGoalProgress(serverLog);
          wlRenderInchesSummary(serverLog);
        } else {
          wlLoadBmiGraph().then(function () {
            const container = qs("[data-wl-bmi-graph-container]");
            if (container && !container.querySelector("svg")) {
              const local = storageLoadBmiLog().map(function (e) { return { date: e.date, weightLb: e.weight_lb }; });
              if (local.length >= 2) {
                const statusEl2 = qs("[data-wl-bmi-graph-status]");
                if (statusEl2) statusEl2.textContent = "Showing entries saved on this device.";
                wlRenderBmiChart(local);
                wlCheckBmiRate(local);
              }
            }
          });
        }
      }

      // Never trap the member on a spinner — sends are fire-and-forget with
      // a short grace so the requests get out before any navigation.
      Promise.allSettled(done).finally(finish);
      setTimeout(finish, 3500);
    });

    render();
  }

  /* Reward card boot: the bmi fetch normally only runs inside modals or
     after a check-in, so on a plain page load the card would sit hidden
     forever. One light fetch on load renders it (members only - the card
     markup exists solely in the wl_access branch). */
  function initReward() {
    if (!qs("[data-wl-reward-card]")) return;
    fetch("/apps/wl-hub/bmi")
      .then(function (res) { return res.json().catch(function () { return {}; }).then(function (data) {
        if (res.ok && data.ok) wlRenderReward(data.log || [], data.reward);
      }); })
      .catch(function (err) { console.error("[WL] Reward card load failed:", err); });
  }

  // -----------------------------
  // Lab uploads (Derek 2026-09-01)
  //
  // The card is hidden in the markup and only revealed once the server confirms
  // Dropbox is configured, so this could ship before the credentials existed
  // without ever showing a button that throws.
  //
  // PROGRAM is hard-coded per portal — this is the WEIGHT-LOSS build, so it
  // posts "wl" and the server files everything under /Weight Loss Program/.
  // The hormone portal's copy of this block passes "hrt"; the data-hrt-labs-*
  // attribute names are kept identical on purpose so the two stay a
  // straight copy of each other — the portals never render together, so the
  // names cannot collide.
  // -----------------------------
  const HRT_LABS_PROGRAM = "wl";
  let hrtLabFiles = [];
  let hrtLabsLimits = { maxFiles: 8, maxBytes: 25 * 1024 * 1024 };

  function hrtLabsFmtSize(bytes) {
    if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + " MB";
    return Math.max(1, Math.round(bytes / 1024)) + " KB";
  }

  function hrtLabsRender() {
    const list = qs("[data-hrt-labs-list]");
    const send = qs("[data-hrt-labs-send]");
    if (!list || !send) return;

    if (!hrtLabFiles.length) {
      list.style.display = "none";
      list.innerHTML = "";
      send.disabled = true;
      send.style.opacity = ".45";
      send.style.cursor = "not-allowed";
      send.textContent = "Send my labs";
      return;
    }

    list.style.display = "block";
    list.innerHTML = hrtLabFiles.map(function (f, i) {
      const over = f.size > hrtLabsLimits.maxBytes;
      return '<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;' +
             'padding:9px 12px;border:1px solid ' + (over ? "#f2b8b5" : "#e5e7eb") + ';' +
             'background:' + (over ? "#fdf3f2" : "#fff") + ';margin-bottom:6px;">' +
               '<span style="font-size:16px;color:#122a44;word-break:break-all;">' + f.name + '</span>' +
               '<span style="font-size:16px;color:' + (over ? "#b3261e" : "#8a8f99") + ';white-space:nowrap;">' +
                 hrtLabsFmtSize(f.size) + (over ? " — too big" : "") +
                 ' <button type="button" data-hrt-labs-remove="' + i + '" aria-label="Remove" ' +
                 'style="border:0;background:none;color:#8a8f99;font-size:19px;line-height:1;cursor:pointer;padding:0 0 0 8px;">&times;</button>' +
               '</span>' +
             '</div>';
    }).join("");

    const sendable = hrtLabFiles.filter(function (f) { return f.size <= hrtLabsLimits.maxBytes; });
    send.disabled = sendable.length === 0;
    send.style.opacity = sendable.length ? "1" : ".45";
    send.style.cursor = sendable.length ? "pointer" : "not-allowed";
    send.textContent = sendable.length === 1 ? "Send 1 file" : "Send " + sendable.length + " files";
  }

  function initLabUploads() {
    const card = qs("[data-hrt-labs-card]");
    if (!card) return;
    const input = qs("[data-hrt-labs-input]");
    const statusEl = qs("[data-hrt-labs-status]");

    // Only reveal the card once the server says uploads actually work.
    fetch("/apps/wl-hub/labs")
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.ok || !d.configured) return;
        hrtLabsLimits = { maxFiles: d.maxFiles || 8, maxBytes: d.maxBytes || hrtLabsLimits.maxBytes };
        card.style.display = "block";
        if (input && Array.isArray(d.accept)) {
          input.setAttribute("accept", d.accept.map(function (e) { return "." + e; }).join(","));
        }
      })
      .catch(function () { /* leave hidden */ });

    if (input) {
      input.addEventListener("change", function () {
        const picked = Array.prototype.slice.call(input.files || []);
        hrtLabFiles = hrtLabFiles.concat(picked).slice(0, hrtLabsLimits.maxFiles);
        input.value = "";           // so re-picking the same file still fires change
        if (statusEl) statusEl.textContent = "";
        hrtLabsRender();
      });
    }

    document.addEventListener("click", function (e) {
      const rm = e.target.closest("[data-hrt-labs-remove]");
      if (rm) {
        hrtLabFiles.splice(parseInt(rm.getAttribute("data-hrt-labs-remove"), 10), 1);
        hrtLabsRender();
        return;
      }

      const send = e.target.closest("[data-hrt-labs-send]");
      if (!send || send.disabled) return;

      const files = hrtLabFiles.filter(function (f) { return f.size <= hrtLabsLimits.maxBytes; });
      if (!files.length) return;

      const fd = new FormData();
      fd.append("program", HRT_LABS_PROGRAM);
      files.forEach(function (f) { fd.append("files", f, f.name); });

      send.disabled = true;
      send.style.opacity = ".45";
      send.textContent = "Sending…";
      if (statusEl) statusEl.textContent = "Uploading — please keep this page open.";

      fetch("/apps/wl-hub/labs", { method: "POST", body: fd })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (d) {
          const okCount = (d.uploaded || []).length;
          const bad = d.rejected || [];
          if (okCount) {
            // Clear only what actually landed, so a partial failure leaves the
            // failed files staged for a retry instead of silently vanishing.
            // Keep ONLY the files the server rejected, so a partial failure
            // leaves those staged for a retry while the successful ones clear.
            // Matched on the rejected list because the server returns SAFE
            // (sanitised) names for successes, which need not equal f.name.
            hrtLabFiles = hrtLabFiles.filter(function (f) {
              return bad.some(function (b) { return b.fileName === f.name; });
            });
          }
          hrtLabsRender();
          if (statusEl) {
            let msg = "";
            if (okCount) {
              msg = "<strong style=\"color:#1e7a4d;\">Got them — " + okCount + " file" +
                    (okCount > 1 ? "s" : "") + " sent to your care team.</strong>";
            }
            if (bad.length) {
              msg += (msg ? "<br>" : "") + bad.map(function (b) {
                return "<span style=\"color:#b45309;\">" + b.fileName + " — " + b.reason + "</span>";
              }).join("<br>");
            }
            if (!okCount && !bad.length) {
              msg = "<span style=\"color:#b45309;\">" + (d.error || "That didn't go through — please try again, or send them in chat.") + "</span>";
            }
            statusEl.innerHTML = msg;
          }
        })
        .catch(function () {
          if (statusEl) statusEl.innerHTML = '<span style="color:#b45309;">That did not go through &mdash; please try again, or send them in chat.</span>';
          hrtLabsRender();
        });
    });
  }

  // -----------------------------
  // Boot
  // -----------------------------
  document.addEventListener("DOMContentLoaded", function () {
    initMotivation();
    initLabUploads();
    initCheckin();
    initModals();
    initReward();
    initBmi();
    initBmr();
    initMacros();
    initMacroSave();
    initDose();
    initDoseCalculator();
    initAddons();
    initPeptideQuiz();
    initRivoRewards();
    initTelemedCompliance();

    // If any modals are open by default (unlikely), render bmi log + macro calories
    renderBmiLog();
    refreshMacroCaloriesUI();
  });

  // Chat click binding lives here deliberately — a synchronous top-level
  // statement at the very end of the IIFE, NOT inside the DOMContentLoaded
  // block above. Matches the working pattern from
  // wl-post-visit-checkout.js exactly (its chat listener is also a
  // top-level statement positioned right before that file's own EXPORTS
  // section, at the end of its IIFE) — confirmed by Derek as the detail
  // that made this work on that page.
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-wl-open-chat]");
    if (!btn) return;
    e.preventDefault();

    if (wlOpenGhlChat()) return;

    const originalText = btn.textContent;
    btn.textContent = "Opening chat…";

    let attemptsLeft = 40;
    const retry = setInterval(() => {
      if (wlOpenGhlChat() || attemptsLeft <= 0) {
        clearInterval(retry);
        btn.textContent = originalText;
        if (attemptsLeft <= 0) {
          console.warn("[WL] Chat widget never finished loading — please try again.");
        }
        return;
      }
      attemptsLeft -= 1;
    }, 500);
  });
})();



